import { useEffect, useId, Fragment } from 'react'
import { useRouter } from 'next/navigation'
import TableOfContents from './TableOfContents'
import YilanTeacherPerks from './YilanTeacherPerks'
import RouterUxOptimization from './RouterUxOptimization'
// import ISupportServiceSystem from './ISupportServiceSystem'
import { SharedProjectHero, SharedProjectMetaBar } from './SharedProjectHero'
import { Eye, ArrowLeft } from 'lucide-react'

interface Project {
  id: string
  title: string
  category: string
  tagline: string
  role: string
  timeline: string
  platform: string
  problem: string
  solution: string
  research: string
  validation: string
  color: string
  draft?: boolean
}

interface WorkViewProps {
  onProjectSelect?: (isSelected: boolean) => void
  deselectFlag?: number
  initialSlug?: string
}

export default function WorkView({ onProjectSelect, deselectFlag, initialSlug }: WorkViewProps) {
  const router = useRouter()
  const styleId = useId()

  // Inject mobile override styles for inline grids in case study detail views
  // This is needed because inline styles have higher specificity than CSS classes
  useEffect(() => {
    const styleEl = document.createElement('style')
    styleEl.id = `mobile-grid-overrides-${styleId}`
    styleEl.textContent = `
      @media (max-width: 768px) {
        /* New Hero Mobile Overrides */
        .new-hero-container {
          grid-template-columns: 1fr !important;
          padding: 6rem 1.5rem 3rem !important;
        }
        .new-hero-gradient {
          background: linear-gradient(180deg, rgba(17,24,39,0.95) 0%, rgba(17,24,39,0.8) 60%, rgba(17,24,39,0.3) 100%) !important;
        }
        .new-hero-stat-wrapper {
          justify-content: flex-start !important;
          margin-top: 2rem !important;
          width: 100% !important;
        }
        .new-hero-stat-card {
          width: 100% !important;
          padding: 1.5rem 0 0 0 !important;
          gap: 1rem !important;
          justify-content: flex-start !important;
        }
        .new-hero-stat-item-num {
          font-size: 1.5rem !important;
        }
        .new-meta-bar {
          grid-template-columns: 1fr !important;
          padding: 1.5rem 0 !important;
          gap: 1.5rem !important;
        }

        /* Collapse all inline grid-template-columns to single column on mobile */
        .case-study-body [style*="grid-template-columns"] {
          grid-template-columns: 1fr !important;
        }
        /* Keep 2-column for 4-column grids (impact numbers) */
        .case-study-body [style*="grid-template-columns: repeat(4, 1fr)"] {
          grid-template-columns: repeat(2, 1fr) !important;
        }
        /* Reduce hero font sizes */
        .case-study-hero-title {
          font-size: 1.6rem !important;
        }
        .case-study-hero-overlay {
          padding: 6.5rem 1.25rem 2rem !important; /* Increase top padding to prevent header from overlapping text */
        }
        .case-study-hero--with-cover {
          min-height: 280px !important;
        }
        /* Reduce section title sizes */
        .case-study-section-title {
          font-size: 1.35rem !important;
        }
        /* Reduce body text */
        .case-study-body p {
          font-size: 0.95rem !important;
        }
        /* Impact number sizes */
        .case-study-body [style*="font-size: 3.6rem"] {
          font-size: 2.4rem !important;
        }
        .case-study-body [style*="font-size: 4.5rem"] {
          font-size: 3rem !important;
        }
        .case-study-body [style*="font-size: 2.4rem"] {
          font-size: 1.8rem !important;
        }
        /* MVA flow diagram */
        .case-study-body [style*="max-width: 720px"] {
          max-width: 100% !important;
          padding: 1rem !important;
        }
        /* Writing items */
        .writing-item {
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 0.75rem !important;
          padding: 1.25rem 1rem !important;
        }
        .writing-item [style*="padding-right"] {
          padding-right: 0 !important;
        }
        /* Filter bar scrollable */
        .work-filter-bar {
          overflow-x: auto !important;
          flex-wrap: nowrap !important;
          gap: 1rem !important;
        }
        .filter-btn {
          white-space: nowrap !important;
          flex-shrink: 0 !important;
        }
        /* Publications section full width fix */
        .publications-section {
          width: 100% !important;
          margin-left: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          box-sizing: border-box !important;
        }
        /* About section full width fix — edge-to-edge background, content padding via .about-container */
        #about.port-section-wrapper {
          width: 100% !important;
          margin-left: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        /* Task testing items: stack text + image vertically on mobile */
        .case-study-body [style*="flex-direction: row"][style*="align-items: center"] {
          flex-direction: column !important;
          align-items: stretch !important;
        }
        .case-study-body [style*="flex-direction: row"][style*="align-items: center"] > div {
          flex: none !important;
          width: 100% !important;
        }
        /* Six Usability Issues: stack vertically on mobile */
        .usability-issue-row {
          flex-direction: column !important;
          gap: 0.75rem !important;
        }
        .usability-issue-badge {
          flex: none !important;
          flex-direction: row !important;
          gap: 0.5rem !important;
          justify-content: flex-start !important;
          width: 100% !important;
        }
        .usability-issue-content {
          flex: none !important;
          width: 100% !important;
        }
      }
    `
    document.head.appendChild(styleEl)
    return () => {
      const existing = document.getElementById(`mobile-grid-overrides-${styleId}`)
      if (existing) existing.remove()
    }
  }, [styleId])

  // Notify parent when a project is selected/deselected
  useEffect(() => {
    onProjectSelect?.(!!initialSlug)
  }, [initialSlug, onProjectSelect])

  // When parent requests deselect (e.g. navbar click to Research/About while project is open)
  useEffect(() => {
    if (deselectFlag && deselectFlag > 0) {
      if (initialSlug) {
        router.push('/')
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }, [deselectFlag, initialSlug, router])

  const projects: Project[] = [
    {
      id: 'sanching-digital-service',
      title: '0 ➔ 1 宮廟服務數位轉型',
      category: '2024-2026・服務設計・UI/UX 設計・App + Web + LINE',
      tagline:
        '協助臺灣歷史悠久的道教宮廟將長期依賴紙本、人工與資深人員經驗的服務模式，轉型為可追溯、易維護且具擴充性的數位服務系統。',
      role: '專案經理、產品設計師',
      timeline: '2024.05 — 2026.02',
      platform: '響應式網頁 / LINE 官方帳號 / 平板 App',
      problem:
        '傳統宮廟服務高度依賴紙本、人工作業與資深人員經驗。在管理層、專職人員、志工與信眾之間，存在顯著的協同作業、資訊溝通、服務旅程及線上線下整合落差，導致行政負擔沉重且數位化成效不佳。',
      solution:
        '透過服務藍圖與田野研究重新設計整體服務架構，整合後台管理系統、LINE 官方帳號與前台線上報名系統。將原本分散的紙本、現場、電話及人工對帳流程，轉化為可追溯、易維護的數位服務系統。',
      research:
        '執行 21 場／共 24 小時的深度訪談（涵蓋管理層、幹部、專職人員、志工與信眾），並回收 300+ 份問卷。分析 14 萬字訪談逐字稿，產出利害關係人地圖、使用者旅程圖與服務藍圖。',
      validation:
        '達成 52.2% 數位服務替代率。LINE 官方帳號吸引 9,200+ 名好友加入，累積 6,100+ 筆線上報名紀錄，交易金額突破新臺幣 1,000 萬元。信眾滿意度達 4.4/5（61 歲以上族群滿意度達 4.3/5）。將 18 個人工處理節點整合為 6 個（觸點數量減少 66.7%）。榮獲 IASDR 2025 最佳論文獎（Best Paper）。',
      color: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    },

    {
      id: 'residential-gateway-ux-optimization',
      title: '中華電信路由器管理介面優化',
      category: '2026.07・Product Design・UX Research・UI Design',
      tagline:
        'A UX research project redesigning the settings interface of Chunghwa Telecom\'s home fiber router ("小烏龜") to make it usable for non-technical household users.',
      role: 'UX Researcher',
      timeline: '2026.07',
      platform: 'UX Research & Tree Testing',
      problem:
        'Router admin panel interface built around engineering categories rather than real user tasks.',
      solution:
        'Restructured interface based on heuristic evaluation, card sorting, and tree testing across 6 personas.',
      research:
        'Evaluated 10 heuristics, tested 10 core tasks across 6 personas using AI simulation and custom tree test tool.',
      validation:
        'Achieved 66.7% average success rate and identified key IA gaps in sub-menu terminology.',
      color: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    },

    {
      id: 'yilan-teacher-perks',
      title: '宜蘭教育動｜教師優惠券服務設計',
      category: '2024・Product Design・UI/UX Design・App',
      tagline:
        '為宜蘭縣教職員提供瀏覽、兌換、轉贈專屬優惠券及查詢紀錄的功能。透過清晰的狀態顯示與操作流程，讓教職員能快速掌握優惠內容與使用方式。',
      role: 'UI/UX 設計師',
      timeline: '2024',
      platform: '行動網頁 App',
      problem:
        '為宜蘭縣教職員設計專屬優惠券功能。透過清晰的資訊層級、明確的狀態標示與溫暖親切的視覺語言，重新設計從瀏覽、兌換、轉贈到歷史紀錄的全流程體驗。',
      solution:
        '詳細頁分為主要優惠、關鍵欄位、使用須知三大區塊。減少大段文字，改用圖示卡片提升可掃讀性。使用色彩、圖示、文字三重編碼呈現狀態。',
      research:
        '考量公務繁忙的教職員，提供專業可信且易於快速掃讀的介面設計，並針對轉贈功能加入二次確認機制，防止誤操作。',
      validation:
        '共用元件包含卡片、狀態標籤、資訊欄、彈窗及成功/失敗結果頁。跨畫面體驗一致且可預期，提升繁體中文閱讀舒適度。',
      color: 'linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%)',
    },

    /*
    {
      id: 'bookstore-usability-research',
      title: 'Usability Study of Three Major Bookstore E-commerce Websites in Taiwan',
      category: '2025・UX Research・Usability Testing',
      tagline:
        'Compared the usability performance of Eslite Online, Books.com.tw, and Kingstone using task testing, Think Aloud protocol, SUS scale, and One-way ANOVA.',
      role: 'UX Researcher',
      timeline: '2025',
      platform: 'Usability Research · Task Testing · SUS · ANOVA',
      problem:
        "The three major bookstore e-commerce websites in Taiwan (Eslite Online, Books.com.tw, Kingstone) are the primary channels for consumers to purchase books. However, are there significant differences in interface usability? What design factors affect users' operational efficiency and satisfaction? There is currently a lack of systematic comparative research using standardized usability testing methods.",
      solution:
        'Employed a within-subjects experimental design, recruiting 30 participants aged 20–59 with online book purchasing experience to perform five core tasks (search for books, add to cart, checkout process, view orders, use collection feature) on the three platforms. Simultaneously collected operation time, task success rate, SUS scale, and qualitative feedback, using One-way ANOVA to test for significant differences between platforms.',
      research:
        '30 participants (aged 20–59, all with online book purchasing experience). Employed Task Testing, Think Aloud Protocol, System Usability Scale (SUS), and One-way ANOVA across five core tasks on the three platforms.',
      validation:
        'The study found statistically significant differences (p < .05) in 3 out of 5 tasks among the three platforms. The operation time for the collection feature had the largest difference (2.9 times), with speed bottlenecks existing in the shopping cart and checkout processes. The average SUS score fell in the "Marginal" to "Good" range (M = 68.2), and ANOVA results showed no significant differences between platforms (F(2, 87) = 2.31, p = .105).',
      color: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
    },
    */
    {
      id: 'post-office-ux-research',
      title: 'Usability Study: Chunghwa Post Global Website',
      category: '2024・UX Research・Web',
      tagline:
        'Utilized the Think Aloud protocol to identify usability issues in the information architecture and process guidance of the Chunghwa Post website.',
      role: 'Lead Researcher',
      timeline: '2024',
      platform: 'Usability Research & Think Aloud Protocol',
      problem:
        'Chunghwa Post is the most widely covered postal service organization in Taiwan, and its official website is the core touchpoint for its digital transformation. However, when users need to complete complex tasks, does the website truly help them quickly understand procedures, find information, and prepare?',
      solution:
        'We focused on the real-world scenario of "preparing to send an international parcel." Through the Think Aloud protocol, we observed users\' operation processes to identify usability issues in information architecture, content presentation, process guidance, and interface interaction, proposing six design improvement directions.',
      research:
        '6 users aged 20–30 without international postal experience. Using the Think Aloud Protocol, they performed three core tasks under the scenario of "sending a Christmas gift from Taiwan to a Dutch friend": checking postage rates, understanding required documents, and preparing the English-translated address.',
      validation:
        'The research revealed that users could not establish a clear mental model on the website—they did not know where information was, where they were, or what to do next. Six insights pointed to the same fundamental problem: insufficient information architecture and process guidance.',
      color: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    },

    {
      id: 'laojian-brand-research',
      title: '讓青草茶走進日常｜品牌再定位研究',
      category: '2024.09－2024.11・Brand Study・Customer Experience・UX Research',
      tagline:
        '一項結合數位與實體的轉型研究，探討萬華傳統青草店如何透過體驗設計與品牌重組吸引年輕世代。',
      role: 'UX 研究員 & 品牌策略師',
      timeline: '3 個月 (2024)',
      platform: '質性 UX 研究 & 品牌定位',
      problem:
        '傳統萬華青草店面臨客群老化與漢方草本文化認同淡化的挑戰。年輕人對青草茶的認知侷限於傳統退火解熱，缺乏與日常保健及現代茶飲的連結。',
      solution:
        '我們在青草巷進行深度的田野研究、繪製顧客體驗旅程，並對年輕客群進行調查。提煉出「漢方療癒飲品學」，重組店內服務流程，設計情境導向的茶飲菜單與青草 DIY 手作體驗。',
      research:
        '對 8 名目標年輕受眾進行體驗測試，分析他們對青草包裝、沖泡儀式與視覺語彙的心智表徵，繪製出「老店新生體驗旅程圖」。',
      validation:
        '經過新品牌概念測試後，年輕顧客對青草茶品牌的偏好度提升了 75%，與日常飲用情境的連結感增長了 120%。',
      color: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    },
  ]

  const renderMockup = (id: string) => {
    let src = ''
    if (id === 'sanching-digital-service') {
      src = '/images/temple work cover image.png'
    } else if (id === 'residential-gateway-ux-optimization') {
      src = '/images/Residential Gateway-Product Card.png'
    } else if (id === 'bookstore-usability-research') {
      src = '/images/online bookstore work.jpg'
    } else if (id === 'post-office-ux-research') {
      src = '/images/Post Office work.jpg'
    } else if (id === 'laojian-brand-research') {
      src = '/images/Healing Herbar Store.jpg'
    } else if (id === 'yilan-teacher-perks') {
      src = '/images/yilan-teacher-coupon.png'
    }
    // [Commented out] iSupport Service System mockup
    // else if (id === 'isupport-service-system') {
    //   src = '/images/isupport Work Photo.jpg'
    // }
    else {
      src =
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=800&auto=format&fit=crop'
    }

    return (
      <div className="mockup-img-container">
        <img src={src} alt={`${id} UI Mockup`} className="mockup-img" />
      </div>
    )
  }

  const selectedProject = initialSlug ? projects.find((p) => p.id === initialSlug) || null : null

  // --- RENDERING DETAIL VIEW (FULL PAGE) ---
  if (initialSlug && !selectedProject) {
    return (
      <div className="port-section" style={{ textAlign: 'center', padding: '10rem 2rem' }}>
        <h2
          style={{
            fontSize: '2.5rem',
            marginBottom: '1.5rem',
            fontFamily: 'var(--font-sans)',
            fontWeight: 800,
          }}
        >
          Project Not Found
        </h2>
        <p style={{ color: '#475569', fontSize: '1.15rem', marginBottom: '2rem' }}>
          The project you are looking for does not exist.
        </p>
        <button
          onClick={() => router.push('/')}
          style={{
            background: 'var(--port-text-primary)',
            color: '#fff',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '999px',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Return to Work
        </button>
      </div>
    )
  }

  if (selectedProject) {
    const proj = selectedProject
    const isTemple = proj.id === 'sanching-digital-service'
    const isHealingHerbar = proj.id === 'laojian-brand-research'
    const isApex = proj.id === 'bookstore-usability-research'
    const isYilan = proj.id === 'yilan-teacher-perks'
    const isRouter = proj.id === 'residential-gateway-ux-optimization'

    // --- RENDERING DETAIL VIEW FUNCTION ---
    function renderDetailView() {
      if (isRouter) {
        return <RouterUxOptimization proj={proj} />
      }
      if (isTemple) {
        return (
          /* ====== REDESIGNED: 0 ➔ 1 宮廟服務數位轉型 ====== */
          <div className="temple-case-root">
            <style>{`
              .temple-section { margin-top: 4.5rem; margin-bottom: 6rem; }
              .temple-section:first-of-type { margin-top: 0; }
              .temple-section-label { display: none !important; }
              .temple-product-number { display: none !important; }
              .temple-fullbleed-dark {
                width: 100vw;
                margin-left: calc(-50vw + 50%);
                background: #0f0f0f;
                padding: 6rem clamp(1.5rem, 5vw, 4rem);
                position: relative;
                overflow: hidden;
              }
              .temple-fullbleed-dark h2 {
                color: #ffffff;
                font-size: clamp(1.65rem, 2.5vw, 2.15rem);
                font-weight: 700; margin-top: 1rem; margin-bottom: 1.35rem; line-height: 1.3;
              }
              .temple-fullbleed-dark h2:not(:first-child) {
                margin-top: 3rem;
              }
              .temple-fullbleed-dark h3 {
                color: #ffffff;
                font-size: clamp(1.25rem, 1.8vw, 1.45rem);
                font-weight: 700; margin-top: 2.25rem; margin-bottom: 1rem; line-height: 1.4;
              }
              .temple-fullbleed-dark p, .temple-fullbleed-dark li {
                color: rgba(255,255,255,0.85);
                font-size: 1.05rem; line-height: 1.8; margin-bottom: 1.5rem;
              }
              .temple-fullbleed-dark .temple-highlight-num {
                color: #ffffff;
                font-size: clamp(3rem, 6vw, 4.5rem);
                font-weight: 800; line-height: 1;
                letter-spacing: -0.02em;
              }
              .temple-fullbleed-dark .temple-highlight-label {
                color: rgba(255,255,255,0.6);
                font-size: 0.95rem; font-weight: 500; margin-top: 0.4rem;
              }
              .temple-fullbleed-dark .temple-accent-gold { color: #d4a017; }
              .temple-fullbleed-dark .temple-accent-green { color: #34d399; }
              .temple-section-light {
                max-width: 1200px;
                margin: 0 auto;
                padding: 0 clamp(1.5rem, 5vw, 4rem);
              }
              .temple-section-light h2 {
                font-size: clamp(1.65rem, 2.5vw, 2.15rem);
                font-weight: 700; color: #0f172a;
                margin-top: 1rem; margin-bottom: 1.35rem; line-height: 1.3;
              }
              .temple-section-light h2:not(:first-child) {
                margin-top: 3.5rem;
              }
              .temple-section-light h3 {
                font-size: clamp(1.25rem, 1.8vw, 1.45rem);
                font-weight: 700; color: #0f172a; margin-top: 2.5rem; margin-bottom: 1rem; line-height: 1.4;
              }
              .temple-section-light p {
                font-size: 1.05rem; color: #334155;
                line-height: 1.8; margin-bottom: 1.5rem;
              }
              .temple-img-full {
                width: 100vw;
                margin-left: calc(-50vw + 50%);
                display: block;
              }
              .temple-img-full img { width: 100%; height: auto; display: block; }
              .temple-img-caption {
                max-width: 1200px; margin: 1rem auto 0;
                font-size: 0.925rem; color: #64748b;
                text-align: center; font-style: italic;
                padding: 0 1.5rem; line-height: 1.6;
              }
              .temple-img-full-constrained {
                width: 100vw;
                margin-left: calc(-50vw + 50%);
                display: flex; justify-content: center;
                background: #f8f8f6;
              }
              .temple-img-full-constrained img {
                max-height: 70vh; width: auto; max-width: 100%;
                object-fit: contain; display: block;
              }
              .temple-img-diagram {
                max-width: 1200px;
                margin: 0 auto;
                display: flex; justify-content: center;
                padding: 2rem clamp(1.5rem, 5vw, 4rem);
              }
              .temple-img-diagram img {
                width: 100%;
                height: auto;
                border-radius: 12px;
                border: 1px solid #e2e8f0;
                display: block;
              }
              .temple-img-content {
                margin: 2rem 0; border-radius: 12px;
                overflow: hidden; border: 1px solid #e2e8f0;
              }
              .temple-img-content img { width: 100%; height: auto; display: block; }
              .temple-hero {
                position: relative; width: 100vw;
                margin-left: calc(-50vw + 50%);
                min-height: 80vh; display: flex; align-items: center;
                background: #0a0a0a; overflow: hidden;
              }
              .temple-hero-bg { position: absolute; inset: 0; z-index: 0; }
              .temple-hero-bg img { width: 100%; height: 100%; object-fit: cover; }
              .temple-hero-overlay {
                position: absolute; inset: 0;
                background: linear-gradient(135deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.3) 100%);
                z-index: 1;
              }
              .temple-hero-content {
                position: relative; z-index: 2;
                max-width: 1400px; margin: 0 auto;
                padding: 7rem 2rem 5rem; width: 100%;
                text-align: center;
              }
              .temple-hero-content h1 {
                font-size: clamp(2.2rem, 4vw, 3.5rem);
                font-weight: 800; color: #ffffff;
                line-height: 1.18; margin: 0 auto 1.25rem; max-width: 1200px;
              }
              .temple-hero-content p {
                font-size: clamp(1.05rem, 1.6vw, 1.2rem);
                color: rgba(255,255,255,0.85);
                line-height: 1.75; max-width: 1000px; margin: 0 auto 2rem;
              }
              .temple-hero-stats { display: flex; gap: 2.5rem; flex-wrap: wrap; margin-top: 2rem; }
              .temple-hero-stat { text-align: left; flex: 1 1 180px; }
              .temple-hero-stat-value {
                font-size: clamp(1.8rem, 3.5vw, 2.6rem);
                font-weight: 800; color: #ffffff;
                line-height: 1; margin-bottom: 0.25rem;
              }
              .temple-hero-stat-label {
                font-size: 0.9rem; color: rgba(255,255,255,0.8); font-weight: 600;
              }
              .temple-hero-award {
                display: inline-flex; align-items: center; gap: 0.5rem;
                background: rgba(254,243,199,0.15); color: #fcd34d;
                padding: 0.45rem 1.15rem; border-radius: 99px;
                font-size: 0.9rem; font-weight: 700; margin-bottom: 1.25rem;
              }
              .temple-hero-gradient-bottom {
                position: absolute; bottom: 0; left: 0; right: 0; height: 180px;
                background: linear-gradient(to bottom, transparent 0%, #ffffff 100%);
                z-index: 3; pointer-events: none;
              }
              .temple-meta-bar {
                max-width: 1050px; margin: 0 auto;
                display: grid; grid-template-columns: repeat(3, 1fr);
                gap: 2rem; padding: 2rem 0; border-bottom: 1px solid #e5e7eb;
              }
              .temple-meta-label {
                font-size: 0.8rem; font-weight: 700; color: #64748b;
                text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 0.35rem;
              }
              .temple-meta-value { font-size: 1.05rem; font-weight: 700; color: #0f172a; }
              .temple-challenge-flow {
                display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                gap: 1.5rem; margin: 2rem 0;
              }
              .temple-challenge-card {
                background: #ffffff; border: 1px solid #e2e8f0;
                border-radius: 16px; padding: 2rem 1.6rem;
                transition: transform 0.3s ease, box-shadow 0.3s ease;
              }
              .temple-challenge-card:hover {
                transform: translateY(-4px);
                box-shadow: 0 12px 40px rgba(0,0,0,0.06);
              }
              .temple-challenge-card-icon {
                width: 44px; height: 44px; border-radius: 12px;
                display: flex; align-items: center; justify-content: center;
                margin-bottom: 1rem; background: #fef2f2; color: #ef4444;
              }
              .temple-challenge-card h4 {
                font-size: 1.125rem; font-weight: 700; color: #0f172a; margin-bottom: 0.65rem;
              }
              .temple-challenge-card p {
                font-size: 1.05rem; color: #475569; line-height: 1.7; margin: 0;
              }
              .temple-challenge-arrow {
                display: flex; align-items: center; justify-content: center; color: #cbd5e1;
              }
              .temple-core-question {
                background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 2.5rem 2rem;
                text-align: center; margin: 2.5rem 0; border-left: 4px solid #8b5cf6;
              }
              .temple-core-question-label {
                display: none !important;
              }
              .temple-core-question p.temple-core-question-hmw {
                font-size: 12px; font-weight: 800; letter-spacing: 0.15em;
                color: #4f46e5; text-transform: uppercase; margin-bottom: 0.75rem;
                font-family: var(--font-sans); margin-top: 0;
              }
              .temple-core-question p {
                font-size: 1.2rem; font-weight: 600; color: #0f172a;
                line-height: 1.6; margin: 0 auto; max-width: 900px;
              }
              .temple-research-numbers {
                display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                gap: 1.75rem; margin: 2rem 0;
              }
              .temple-research-number-item { text-align: center; padding: 1.75rem 1rem; }
              .temple-research-number-item .num {
                font-size: clamp(2.5rem, 5vw, 3.5rem);
                font-weight: 800; color: #0f172a; line-height: 1; margin-bottom: 0.4rem;
              }
              .temple-research-number-item .label {
                font-size: 1.05rem; color: #1e293b; font-weight: 600;
              }
              .temple-research-number-item .sub {
                font-size: 0.9rem; color: #64748b; margin-top: 0.35rem;
              }
              .temple-research-table-wrap {
                overflow-x: auto; margin: 2rem 0;
                border-radius: 12px; border: 1px solid #e2e8f0;
              }
              .temple-research-table { width: 100%; border-collapse: collapse; font-size: 0.95rem; }
              .temple-research-table th {
                background: #f8fafc; padding: 0.9rem 1.15rem; text-align: left;
                font-weight: 700; color: #0f172a; font-size: 0.925rem;
                border-bottom: 2px solid #cbd5e1; white-space: nowrap;
              }
              .temple-research-table td {
                padding: 0.9rem 1.15rem; border-bottom: 1px solid #e2e8f0; color: #334155; line-height: 1.6;
              }
              .temple-research-table tr:nth-child(even) td { background: #fafaf8; }
              .temple-research-table .phase-cell { font-weight: 700; color: #2d6a4f; white-space: nowrap; }
              .temple-research-table .subject-cell { font-weight: 700; color: #0f172a; }
              .temple-gaps-grid {
                display: grid; grid-template-columns: repeat(2, 1fr);
                gap: 1.25rem; margin: 2rem 0;
              }
              .temple-gap-cell { padding: 2rem 1.75rem; border-radius: 16px; border: 1px solid #e2e8f0; display: flex; flex-direction: column; gap: 0.75rem; }
              .temple-gap-cell:nth-child(1) { background: #fef2f2; border-color: #fecaca; }
              .temple-gap-cell:nth-child(2) { background: #fffbeb; border-color: #fef3c7; }
              .temple-gap-cell:nth-child(3) { background: #eff6ff; border-color: #bfdbfe; }
              .temple-gap-cell:nth-child(4) { background: #f0fdf4; border-color: #bbf7d0; }
              .temple-gap-num { font-size: 0.8rem; font-weight: 800; color: rgba(0,0,0,0.35); letter-spacing: 0.08em; }
              .temple-gap-title { font-size: 1.15rem; font-weight: 700; color: #0f172a; }
              .temple-gap-desc { font-size: 1.05rem; color: #334155; line-height: 1.75; margin: 0; }
              .temple-gap-quote {
                font-size: 0.95rem; color: #64748b; font-style: italic; margin: 0;
                border-left: 3px solid #cbd5e1; padding-left: 0.85rem; line-height: 1.6;
              }
              .temple-strategy-quote {
                font-size: clamp(1.35rem, 2.5vw, 1.85rem);
                font-weight: 700; color: #ffffff; line-height: 1.4;
                text-align: center; max-width: 900px; margin: 0 auto 2rem;
              }
              .temple-strategy-desc { max-width: 900px; margin: 0 auto; text-align: center; font-size: 1.05rem; line-height: 1.8; }
              .temple-product-section { margin-bottom: 4.5rem; }
              .temple-product-section:last-child { margin-bottom: 0; }
              .temple-product-header { margin-bottom: 2rem; }
              .temple-product-header h3 {
                font-size: clamp(1.35rem, 2.2vw, 1.65rem);
                font-weight: 700; color: #0f172a; margin: 0 0 0.75rem;
              }
              .temple-product-header p {
                font-size: 1.05rem; color: #475569; line-height: 1.75; margin: 0; max-width: 900px;
              }
              .temple-product-img-wrap {
                width: 100vw; margin-left: calc(-50vw + 50%);
                background: #f2f2f2; display: flex; justify-content: center;
                padding: 2.25rem 0;
              }
              .temple-product-img-wrap img {
                max-height: 75vh; width: auto; max-width: 100%;
                object-fit: contain; display: block;
              }
              .temple-product-img-standalone {
                width: 100%; max-height: 75vh; object-fit: contain; display: block;
                border-radius: 12px; margin-top: 1.5rem;
              }
              .temple-product-img-three {
                display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                gap: 1.25rem; margin: 2rem 0;
              }
              .temple-product-img-three img {
                width: 100%; height: auto; border-radius: 12px;
                border: 1px solid #e2e8f0; display: block;
              }
              .temple-product-caption {
                font-size: 0.925rem; color: #64748b;
                text-align: center; font-style: italic; margin-top: 0.85rem; line-height: 1.6;
              }
              .temple-product-layout {
                display: grid; grid-template-columns: 1fr 1fr;
                gap: 2.5rem; align-items: center; margin: 2rem 0;
              }
              .temple-product-layout img {
                width: 100%; height: auto; border-radius: 12px;
                border: 1px solid #e2e8f0; display: block;
              }
              .temple-product-layout-text p {
                font-size: 1.05rem; color: #334155; line-height: 1.75; margin: 0 0 1rem;
              }
              .temple-product-layout-text p:last-child { margin-bottom: 0; }
              .temple-roadmap {
                display: flex; flex-direction: column; gap: 0;
                margin: 2rem 0; position: relative;
              }
              .temple-roadmap::before {
                content: ''; position: absolute; left: 24px;
                top: 0; bottom: 0; width: 2px; background: #cbd5e1;
              }
              .temple-roadmap-item {
                display: flex; gap: 1.5rem; padding: 1.5rem 0; position: relative;
              }
              .temple-roadmap-dot {
                width: 48px; height: 48px; border-radius: 50%; flex-shrink: 0;
                display: flex; align-items: center; justify-content: center;
                font-weight: 800; font-size: 1rem; position: relative; z-index: 1;
                border: 3px solid #ffffff; box-shadow: 0 4px 12px rgba(0,0,0,0.08);
              }
              .temple-roadmap-dot.phase1 { background: #dbeafe; color: #2563eb; }
              .temple-roadmap-dot.phase2 { background: #d1fae5; color: #059669; }
              .temple-roadmap-dot.phase3 { background: #fef3c7; color: #d97706; }
              .temple-roadmap-content { flex: 1; padding-top: 0.4rem; }
              .temple-roadmap-content h4 {
                font-size: 1.15rem; font-weight: 700; color: #0f172a; margin: 0 0 0.4rem;
              }
              .temple-roadmap-content p {
                font-size: 1.05rem; color: #334155; line-height: 1.7; margin: 0;
              }
              .temple-roadmap-tag {
                display: inline-block; font-size: 0.775rem; font-weight: 700;
                padding: 0.2rem 0.65rem; border-radius: 6px; margin-top: 0.5rem;
              }
              .temple-roadmap-tag.core { background: #dbeafe; color: #2563eb; }
              .temple-roadmap-tag.expand { background: #fef3c7; color: #d97706; }
              .temple-impact-grid {
                display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
                gap: 1.75rem; margin: 2rem 0;
              }
              .temple-impact-item { text-align: center; padding: 1.75rem 1rem; }
              .temple-impact-item .num {
                font-size: clamp(2.5rem, 5vw, 3.5rem);
                font-weight: 800; color: #ffffff; line-height: 1; margin-bottom: 0.5rem;
              }
              .temple-impact-item .label {
                font-size: 1.05rem; color: rgba(255,255,255,0.8); font-weight: 600;
              }
              .temple-impact-item .sub {
                font-size: 0.9rem; color: rgba(255,255,255,0.5); margin-top: 0.35rem;
              }
              .temple-testimonials {
                display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                gap: 1.5rem; margin: 2rem 0;
              }
              .temple-testimonial {
                background: rgba(255,255,255,0.06);
                border: 1px solid rgba(255,255,255,0.12);
                border-radius: 16px; padding: 1.75rem;
              }
              .temple-testimonial-role {
                font-size: 0.9rem; font-weight: 800; color: #fbbf24; margin-bottom: 0.65rem;
              }
              .temple-testimonial-text {
                font-size: 1.05rem; color: rgba(255,255,255,0.85);
                line-height: 1.7; margin: 0; font-style: italic;
              }
              .temple-reflection-list {
                display: flex; flex-direction: column; gap: 1rem; margin: 1.75rem 0;
              }
              .temple-reflection-item {
                display: flex; gap: 1.15rem; align-items: flex-start;
                padding: 1.35rem 1.5rem; background: #f8fafc;
                border-radius: 14px; border-left: 4px solid #8b5cf6; border: 1px solid #e2e8f0; border-left-width: 4px;
              }
              .temple-reflection-num {
                font-size: 1.5rem; font-weight: 800; color: #8b5cf6;
                line-height: 1; flex-shrink: 0; width: 2rem;
              }
              .temple-reflection-item p {
                font-size: 1.05rem; color: #1e293b; line-height: 1.7; margin: 0;
              }
              .temple-cta-wrap { display: flex; justify-content: center; margin-top: 4rem; margin-bottom: 8rem; }le-impact-item { text-align: center; padding: 2rem 1rem; }
              .temple-impact-item .num {
                font-size: clamp(2.5rem, 5vw, 3.5rem);
                font-weight: 800; color: #ffffff; line-height: 1; margin-bottom: 0.5rem;
              }
              .temple-impact-item .label {
                font-size: 0.95rem; color: rgba(255,255,255,0.6); font-weight: 500;
              }
              .temple-impact-item .sub {
                font-size: 0.85rem; color: rgba(255,255,255,0.4); margin-top: 0.3rem;
              }
              .temple-testimonials {
                display: grid; grid-template-columns: repeat(3, 1fr);
                gap: 1.5rem; margin: 2rem 0;
              }
              .temple-testimonial {
                background: rgba(255,255,255,0.06);
                border: 1px solid rgba(255,255,255,0.1);
                border-radius: 16px; padding: 1.5rem;
              }
              .temple-testimonial-role {
                font-size: 0.85rem; font-weight: 700; color: #d4a017; margin-bottom: 0.5rem;
              }
              .temple-testimonial-text {
                font-size: 0.95rem; color: rgba(255,255,255,0.75);
                line-height: 1.6; margin: 0; font-style: italic;
              }
              .temple-reflection-list {
                display: flex; flex-direction: column; gap: 1rem; margin: 1.5rem 0;
              }
              .temple-reflection-item {
                display: flex; gap: 1rem; align-items: flex-start;
                padding: 1.25rem; background: #f5f5f3;
                border-radius: 12px; border-left: 3px solid #8b5cf6;
              }
              .temple-reflection-num {
                font-size: 1.5rem; font-weight: 800; color: #8b5cf6;
                line-height: 1; flex-shrink: 0; width: 2rem;
              }
              .temple-reflection-item p {
                font-size: 1rem; color: #1f2937; line-height: 1.6; margin: 0;
              }
              .temple-cta-wrap { display: flex; justify-content: center; margin-top: 4rem; margin-bottom: 8rem; }
              .temple-fade-in {
                opacity: 0; transform: translateY(30px);
                animation: templeFadeUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              }
              .temple-fade-in-d1 { animation-delay: 0.1s; }
              .temple-fade-in-d2 { animation-delay: 0.2s; }
              .temple-fade-in-d3 { animation-delay: 0.3s; }
              .temple-fade-in-d4 { animation-delay: 0.4s; }
              .temple-fade-in-d5 { animation-delay: 0.5s; }
              @keyframes templeFadeUp { to { opacity: 1; transform: translateY(0); } }
              @media (max-width: 768px) {
                .temple-hero {
                  min-height: auto !important;
                  align-items: flex-start !important;
                  padding-bottom: 6rem !important;
                }
                .temple-hero-bg {
                  position: absolute !important;
                  inset: 0 !important;
                  width: 100% !important;
                  height: 100% !important;
                }
                .temple-hero-bg img {
                  width: 100% !important;
                  height: 100% !important;
                  object-fit: cover !important;
                  object-position: center top !important;
                }
                .temple-hero-overlay {
                  background: linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.80) 100%) !important;
                }
                .temple-hero-content {
                  padding: 6.75rem 1.25rem 2.5rem !important;
                  text-align: center !important;
                }
                .temple-hero-content h1 {
                  font-size: clamp(1.8rem, 6vw, 2.5rem) !important;
                  font-weight: 800 !important;
                  color: #ffffff !important;
                  line-height: 1.25 !important;
                  margin-bottom: 1.25rem !important;
                  letter-spacing: -0.02em !important;
                  max-width: 800px !important;
                  margin-left: auto !important;
                  margin-right: auto !important;
                }
                .temple-hero-content p {
                  font-size: 1rem !important;
                  color: #94a3b8 !important;
                  line-height: 1.7 !important;
                  max-width: 800px !important;
                  margin: 0 auto 2.5rem !important;
                }
                .temple-hero-award {
                  font-size: 0.85rem !important;
                  font-weight: 700 !important;
                  color: #fcd34d !important;
                  letter-spacing: 0.05em !important;
                  background: rgba(254, 243, 199, 0.15) !important;
                  padding: 6px 16px !important;
                  border-radius: 99px !important;
                  display: inline-flex !important;
                  margin-bottom: 1.25rem !important;
                }
                .temple-hero-stats {
                  display: grid !important;
                  grid-template-columns: 1fr 1fr !important;
                  gap: 1.75rem 1.25rem !important;
                  margin-top: 2.5rem !important;
                  margin-left: auto !important;
                  margin-right: auto !important;
                  max-width: 480px !important;
                  width: 100% !important;
                }
                .temple-hero-stat {
                  flex: none !important;
                  text-align: center !important;
                  display: flex !important;
                  flex-direction: column !important;
                  align-items: center !important;
                  justify-content: center !important;
                }
                .temple-hero-stat-value {
                  font-size: clamp(1.75rem, 6vw, 2.2rem) !important;
                  color: #ffffff !important;
                  font-weight: 700 !important;
                  margin-bottom: 0.35rem !important;
                }
                .temple-hero-stat-label {
                  font-size: 0.875rem !important;
                  color: #e2e8f0 !important;
                  font-weight: 600 !important;
                }
                .temple-hero-stat div:last-child {
                  font-size: 0.8rem !important;
                  color: #94a3b8 !important;
                }
                .temple-hero-stat-divider {
                  display: none !important;
                }
                .temple-hero-gradient-bottom {
                  display: block !important;
                }
                .temple-challenge-flow { grid-template-columns: 1fr; }
                .temple-challenge-arrow { transform: rotate(90deg); }
                .temple-research-numbers {
                  grid-template-columns: repeat(2, 1fr) !important;
                  gap: 1.25rem 1rem !important;
                }
                .temple-research-number-item:last-child {
                  grid-column: span 2 !important;
                  max-width: 320px !important;
                  margin: 0 auto !important;
                  width: 100% !important;
                }
                .temple-gaps-grid { grid-template-columns: 1fr; }
                .temple-product-layout { grid-template-columns: 1fr; gap: 1.5rem; }
                .temple-product-img-three { grid-template-columns: 1fr; }
                .temple-impact-grid {
                  display: grid !important;
                  grid-template-columns: repeat(2, 1fr) !important;
                  gap: 2rem 1rem !important;
                }
                .temple-impact-grid > div {
                  padding: 0 0.25rem !important;
                }
                .temple-impact-grid > div > div:first-child {
                  font-size: clamp(1.8rem, 6vw, 2.3rem) !important;
                }
                .temple-impact-grid > div > div:nth-child(2) {
                  font-size: 0.9rem !important;
                }
                .temple-impact-grid > div > div:nth-child(3) {
                  font-size: 0.775rem !important;
                }
                .temple-time-change-item {
                  flex-direction: column !important;
                  align-items: flex-start !important;
                  gap: 0.75rem !important;
                }
                .temple-time-change-item .time-val {
                  text-align: left !important;
                  align-self: flex-start !important;
                  margin-top: 0.25rem !important;
                }
                .temple-testimonials { grid-template-columns: 1fr; }
                .temple-meta-bar { grid-template-columns: 1fr; gap: 1rem; }
                .temple-roadmap::before { left: 18px; }
                .temple-roadmap-dot { width: 36px; height: 36px; font-size: 0.8rem; }
                .temple-line-mockups-wrap {
                  flex-wrap: nowrap !important;
                  overflow-x: auto !important;
                  justify-content: flex-start !important;
                  gap: 0.75rem !important;
                  padding: 0.5rem 1rem 1.25rem !important;
                  width: 100% !important;
                  max-width: 100% !important;
                  -webkit-overflow-scrolling: touch !important;
                  scroll-snap-type: x mandatory !important;
                }
                .temple-line-mockups-wrap > div {
                  flex-shrink: 0 !important;
                  width: 220px !important;
                  min-width: 220px !important;
                  max-width: 220px !important;
                  scroll-snap-align: start !important;
                }
                .temple-line-mockups-wrap > div > div:first-child {
                  aspect-ratio: auto !important;
                  height: auto !important;
                }
                .temple-line-mockups-wrap > div > div:first-child img {
                  position: relative !important;
                  inset: auto !important;
                  width: 100% !important;
                  height: auto !important;
                  display: block !important;
                }
                .temple-line-mockups-wrap > div:nth-child(2) {
                  width: auto !important;
                  min-width: auto !important;
                  max-width: auto !important;
                  padding: 0 0.15rem !important;
                  font-size: 1.5rem !important;
                }
              }
            `}</style>
            {/* HERO — Restore 100% Original Classic Temple Hero Layout with Enhanced Height & Readability */}
            <section className="temple-hero" style={{ minHeight: '85vh', paddingBottom: '8rem' }}>
              <div className="temple-hero-bg">
                <img src="/images/temple3.jpg" alt="Temple interior" />
              </div>
              <div
                className="temple-hero-overlay"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.4) 100%)',
                }}
              />
              <div
                className="temple-hero-content"
                style={{ paddingBottom: '4rem', textAlign: 'center' }}
              >
                <div className="temple-hero-award">🏆 IASDR 2025 最佳論文獎 (Best Paper)</div>
                <h1>0 ➔ 1 宮廟服務數位轉型</h1>
                <p>
                  協助臺灣歷史悠久的道教宮廟將長期依賴紙本、人工與資深人員經驗的服務模式，轉型為可追溯、易維護且具擴充性的數位服務系統。
                </p>
                {/* Refined, Elegant Impact Stats Row with Vertical Dividers */}
                <div
                  className="temple-hero-stats"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '1.5rem',
                    marginTop: '3.5rem',
                    maxWidth: '1160px',
                    margin: '3.5rem auto 0',
                  }}
                >
                  <div className="temple-hero-stat">
                    <div
                      className="temple-hero-stat-value"
                      style={{
                        color: '#ffffff',
                        fontSize: 'clamp(2rem, 3.2vw, 2.75rem)',
                        fontWeight: 600,
                        lineHeight: 1.1,
                        marginBottom: '0.4rem',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      NT$10M+
                    </div>
                    <div
                      className="temple-hero-stat-label"
                      style={{
                        color: '#e2e8f0',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        letterSpacing: '0.01em',
                      }}
                    >
                      累積交易金額
                    </div>
                    <div
                      style={{
                        fontSize: '0.775rem',
                        color: '#94a3b8',
                        marginTop: '0.15rem',
                        fontWeight: 400,
                      }}
                    >
                      線上服務總額
                    </div>
                  </div>

                  <div
                    className="temple-hero-stat-divider"
                    style={{
                      width: '1px',
                      height: '42px',
                      backgroundColor: 'rgba(255,255,255,0.18)',
                      alignSelf: 'center',
                    }}
                  />

                  <div className="temple-hero-stat">
                    <div
                      className="temple-hero-stat-value"
                      style={{
                        color: '#ffffff',
                        fontSize: 'clamp(2rem, 3.2vw, 2.75rem)',
                        fontWeight: 600,
                        lineHeight: 1.1,
                        marginBottom: '0.4rem',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      52.2%
                    </div>
                    <div
                      className="temple-hero-stat-label"
                      style={{
                        color: '#e2e8f0',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        letterSpacing: '0.01em',
                      }}
                    >
                      數位服務替代率
                    </div>
                    <div
                      style={{
                        fontSize: '0.775rem',
                        color: '#94a3b8',
                        marginTop: '0.15rem',
                        fontWeight: 400,
                      }}
                    >
                      紙本轉為數位處理
                    </div>
                  </div>

                  <div
                    className="temple-hero-stat-divider"
                    style={{
                      width: '1px',
                      height: '42px',
                      backgroundColor: 'rgba(255,255,255,0.18)',
                      alignSelf: 'center',
                    }}
                  />

                  <div className="temple-hero-stat">
                    <div
                      className="temple-hero-stat-value"
                      style={{
                        color: '#ffffff',
                        fontSize: 'clamp(2rem, 3.2vw, 2.75rem)',
                        fontWeight: 600,
                        lineHeight: 1.1,
                        marginBottom: '0.4rem',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      66.7%
                    </div>
                    <div
                      className="temple-hero-stat-label"
                      style={{
                        color: '#e2e8f0',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        letterSpacing: '0.01em',
                      }}
                    >
                      接觸點節點精簡
                    </div>
                    <div
                      style={{
                        fontSize: '0.775rem',
                        color: '#94a3b8',
                        marginTop: '0.15rem',
                        fontWeight: 400,
                      }}
                    >
                      從 18 個節點縮減至 6 個
                    </div>
                  </div>

                  <div
                    className="temple-hero-stat-divider"
                    style={{
                      width: '1px',
                      height: '42px',
                      backgroundColor: 'rgba(255,255,255,0.18)',
                      alignSelf: 'center',
                    }}
                  />

                  <div className="temple-hero-stat">
                    <div
                      className="temple-hero-stat-value"
                      style={{
                        color: '#ffffff',
                        fontSize: 'clamp(2rem, 3.2vw, 2.75rem)',
                        fontWeight: 600,
                        lineHeight: 1.1,
                        marginBottom: '0.4rem',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      6,100+
                    </div>
                    <div
                      className="temple-hero-stat-label"
                      style={{
                        color: '#e2e8f0',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        letterSpacing: '0.01em',
                      }}
                    >
                      線上完成報名數
                    </div>
                    <div
                      style={{
                        fontSize: '0.775rem',
                        color: '#94a3b8',
                        marginTop: '0.15rem',
                        fontWeight: 400,
                      }}
                    >
                      信眾自行線上報名
                    </div>
                  </div>
                </div>
                {/* PROJECT METADATA — Integrated Glassmorphism Box */}
                <div
                  className="temple-hero-meta-box"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: '16px',
                    padding: '1.5rem 2rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '1.5rem',
                    textAlign: 'left',
                    maxWidth: '1160px',
                    margin: '3.5rem auto 0',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        color: '#818cf8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: '0.35rem',
                      }}
                    >
                      專案角色
                    </div>
                    <div
                      style={{
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        color: '#ffffff',
                        lineHeight: 1.4,
                      }}
                    >
                      專案經理、產品設計師
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        color: '#818cf8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: '0.35rem',
                      }}
                    >
                      專案時間
                    </div>
                    <div
                      style={{
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        color: '#ffffff',
                        lineHeight: 1.4,
                      }}
                    >
                      2024.05 — 2026.02 (1 年 10 個月)
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        color: '#818cf8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: '0.35rem',
                      }}
                    >
                      專案範疇
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.4rem',
                        marginTop: '0.2rem',
                      }}
                    >
                      {['服務設計', 'UI/UX 設計', 'App + Web + LINE'].map((tag, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#e2e8f0',
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            border: '1px solid rgba(255, 255, 255, 0.18)',
                            padding: '4px 12px',
                            borderRadius: '99px',
                            display: 'inline-block',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="temple-hero-gradient-bottom" />
            </section>

            {/* CHALLENGE */}
            <div
              className="temple-section-light temple-section temple-fade-in"
              style={{ marginTop: '5rem' }}
            >
              <div className="temple-section-label">背景與挑戰</div>
              <h2>三大核心挑戰</h2>
              <p>
                在數位化之前，宮廟的服務流程深植於默會知識（Tacit
                Knowledge）、跨角色溝通落差，以及無法單純透過效率化被削減的宗教民俗文化。
              </p>
              <div className="temple-challenge-flow">
                <div className="temple-challenge-card">
                  <div className="temple-challenge-card-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 2a10 10 0 1 0 10 10h-10V2Z" />
                      <path d="M22 12A10 10 0 0 0 12 2v10h10Z" />
                    </svg>
                  </div>
                  <h4>難以覆盤的默會知識流程</h4>
                  <p>從科儀規範到行政辦理，多數依賴資深專職人員的個人經驗與口頭交接。</p>
                </div>
                <div className="temple-challenge-card">
                  <div className="temple-challenge-card-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                      <path d="M12 18V6" />
                    </svg>
                  </div>
                  <h4>跨角色間的認知落差</h4>
                  <p>管理層、專職人員、志工與信眾對同一個問題往往抱持截然不同的期待與認知。</p>
                </div>
                <div className="temple-challenge-card">
                  <div className="temple-challenge-card-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 2v4M6 4l2 3.5M18 4l-2 3.5" />
                      <path d="M6 20h12" />
                      <path d="M8 16l-2 4" />
                      <path d="M16 16l2 4" />
                      <path d="M4 12h16" />
                    </svg>
                  </div>
                  <h4>流程背後的宗教邏輯</h4>
                  <p>科儀習俗承載著深厚的文化與信仰意義，設計無法單憑追求效率來引導。</p>
                </div>
              </div>
              <div className="temple-core-question">
                <div className="temple-core-question-label">核心提問</div>
                <p className="temple-core-question-hmw">HOW MIGHT WE</p>
                <p>
                  在文化脈絡複雜、多角色協同的環境中，如何設計出一套能真正獲得信賴、被順暢採用並長期維護的數位服務流程？
                </p>
              </div>
            </div>

            {/* RESEARCH */}
            <div
              className="temple-section-light temple-section temple-fade-in"
              style={{ marginTop: '5.5rem' }}
            >
              <div className="temple-section-label">研究與發現</div>
              <h2>深入理解完整服務生態系</h2>
              <p>
                研究從田野實地觀察與利害關係人訪談展開，並在系統上線後進行追蹤回訪以收集真實使用反饋。
              </p>
              <div className="temple-research-numbers">
                <div className="temple-research-number-item">
                  <div className="num">21</div>
                  <div className="label">場深度訪談</div>
                  <div className="sub">累積 24 小時錄音訪談</div>
                </div>
                <div className="temple-research-number-item">
                  <div className="num">300+</div>
                  <div className="label">份問卷回覆</div>
                  <div className="sub">涵蓋四大利害關係人角色</div>
                </div>
                <div className="temple-research-number-item">
                  <div className="num">14萬</div>
                  <div className="label">字逐字稿整理</div>
                  <div className="sub">訪談資料編碼與系統化分析</div>
                </div>
              </div>
              <div className="temple-research-table-wrap">
                <table className="temple-research-table">
                  <thead>
                    <tr>
                      <th>研究階段</th>
                      <th>研究對象</th>
                      <th>研究焦點</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="phase-cell">探索階段</td>
                      <td className="subject-cell">宮廟服務營運管理者</td>
                      <td>管理決策、科儀規劃、跨部門協同</td>
                    </tr>
                    <tr>
                      <td className="phase-cell">探索階段</td>
                      <td className="subject-cell">核心服務執行人員</td>
                      <td>日常執行、報名處理、對帳確認</td>
                    </tr>
                    <tr>
                      <td className="phase-cell">探索階段</td>
                      <td className="subject-cell">服務志工</td>
                      <td>現場引導、臨機協助、資訊轉達</td>
                    </tr>
                    <tr>
                      <td className="phase-cell">探索階段</td>
                      <td className="subject-cell">核心服務使用者 (信眾)</td>
                      <td>資訊獲取、報名辦理、確認反饋體驗</td>
                    </tr>
                    <tr>
                      <td className="phase-cell">後續優化驗證</td>
                      <td className="subject-cell">信眾、專職人員、志工</td>
                      <td>新流程之理解度、易用性與執行落差</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>
                在整理約 14 萬字的訪談資料後，我們將其轉化為利害關係人地圖與使用者旅程圖，歸納出 11
                項服務痛點，首次全面揭露了前台體驗與後台行政營運之間的斷層。
              </p>
            </div>

            {/* FULL-BLEED STAKEHOLDER MAP */}
            <div className="temple-img-diagram">
              <img src="/images/stakeholder map.jpg" alt="Stakeholder Map" />
            </div>
            <p className="temple-img-caption">
              利害關係人地圖 — 實線：正式隸屬／體制指揮關係 ｜ 虛線：溝通協調／體驗協同／服務互動
            </p>

            {/* FULL-BLEED SERVICE BLUEPRINT */}
            <div className="temple-img-diagram">
              <img src="/images/Service Blueprint.jpg" alt="Service Blueprint" />
            </div>
            <p className="temple-img-caption">
              使用者旅程圖 — 展示整合前台信眾體驗、後台行政營運與 LINE 溝通管道的服務流程
            </p>
            {/* KEY INSIGHT — 2x2 Editorial Grid */}
            <div className="temple-section-light temple-section temple-fade-in">
              <div className="temple-section-label">關鍵洞察</div>
              <h2>四大結構性落差</h2>
              <p>
                歸納 11
                項痛點後，我們進一步提煉出四大結構性落差，解釋了過去數位化嘗試未果的核心原因。
              </p>

              {/* System Collaboration Diagram - Before */}
              <div style={{ width: '100%', margin: '2.5rem 0 1rem' }}>
                <img
                  src="/images/System Collaboration Diagram-before.jpeg"
                  alt="Four Structural Gaps Diagram — System Collaboration Diagram (Current State)"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    display: 'block',
                  }}
                />
                <p className="temple-img-caption" style={{ marginTop: '0.75rem', padding: 0 }}>
                  數位化前之服務系統與利害關係人互動關係圖
                </p>
              </div>

              <div className="temple-gaps-grid">
                <div className="temple-gap-cell">
                  <div className="temple-gap-num">GAP 01</div>
                  <div className="temple-gap-title">角色協作落差 (Role Collaboration Gap)</div>
                  <p className="temple-gap-desc">
                    志工與專職人員之間缺乏制度化的分工；許多任務高度依賴口頭交接與個人經驗。
                  </p>
                  <p className="temple-gap-quote">
                    「科儀文疏在列印前都需要志工人工覆核，否則錯誤會一直重複出現。」
                  </p>
                </div>
                <div className="temple-gap-cell">
                  <div className="temple-gap-num">GAP 02</div>
                  <div className="temple-gap-title">
                    資訊溝通落差 (Information Communication Gap)
                  </div>
                  <p className="temple-gap-desc">
                    公告欄、官網、LINE 與現場告示各自散落，信眾難以即時取得準確的科儀與活動資訊。
                  </p>
                  <p className="temple-gap-quote">
                    「我要自己去查網站或跑馬燈才知道課程訊息，覺得效率很低。」
                  </p>
                </div>
                <div className="temple-gap-cell">
                  <div className="temple-gap-num">GAP 03</div>
                  <div className="temple-gap-title">服務旅程落差 (Service Journey Gap)</div>
                  <p className="temple-gap-desc">
                    報名、填表、繳費、確認與通知散落在不同接觸點，導致斷裂的使用者體驗。
                  </p>
                  <p className="temple-gap-quote">
                    「如果信眾還沒繳費，他們也可以用傳真或郵寄的方式辦理。」
                  </p>
                </div>
                <div className="temple-gap-cell">
                  <div className="temple-gap-num">GAP 04</div>
                  <div className="temple-gap-title">虛實整合落差 (Physical-Digital Gap)</div>
                  <p className="temple-gap-desc">
                    前線服務無法即時串聯至後台；資料必須先透過人工收集後再二次登錄。
                  </p>
                  <p className="temple-gap-quote">
                    「櫃檯現場不直接輸入資料。我們必須收集五十份表單，再帶回辦公室統一登錄。」
                  </p>
                </div>
              </div>
            </div>

            {/* SYSTEM STRATEGY — Full-bleed dark */}
            <div className="temple-fullbleed-dark temple-section temple-fade-in">
              <div className="temple-section-label" style={{ textAlign: 'center' }}>
                設計策略
              </div>
              <div className="temple-strategy-quote">
                宮廟服務系統真正需要的，是一套能將人、流程與資訊連結在一起的整合架構。
              </div>
              <div className="temple-strategy-desc">
                <p>
                  基於四大結構性落差，各項設計決策皆對應研究階段所發現的痛點。整體策略聚焦於打造一套同時支援前台信眾體驗、後台行政營運與
                  LINE 溝通管道的整合型服務系統。
                </p>
                <p>
                  系統由三大模組構成：後台管理系統、LINE
                  官方帳號與前台線上報名系統。後台統一數據與服務狀態，LINE
                  作為信眾熟悉的服務入口，前台線上報名系統則處理完整的線上辦理流程。串聯之後，原本分散的流程開始形成可追溯、易維護的數位服務架構。
                </p>
              </div>

              {/* System Collaboration Diagram - After */}
              <div style={{ maxWidth: '1000px', margin: '2rem auto 0' }}>
                <img
                  src="/images/System Collaboration Diagram-after.jpg"
                  alt="System Architecture Diagram"
                  style={{ width: '100%', height: 'auto', borderRadius: '12px', display: 'block' }}
                />
                <p
                  style={{
                    fontSize: '0.85rem',
                    color: 'rgba(255,255,255,0.4)',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    marginTop: '0.75rem',
                  }}
                >
                  本專案整合前後台之服務互動架構圖
                </p>
              </div>
            </div>

            {/* PRODUCT DESIGN — Four Modules */}
            <div className="temple-section-light temple-section temple-fade-in">
              <h2 style={{ marginBottom: '0.75rem' }}>整合模組設計</h2>
              <p style={{ marginBottom: '3.5rem' }}>
                每個模組皆解決特定的結構性落差，同時維持整個服務生態系中順暢的資料流動。
              </p>

              {/* Module 01: Back-end Data Management System */}
              <div className="temple-product-section">
                <div className="temple-product-header">
                  <h3>
                    <span style={{ color: '#2563eb', marginRight: '0.5rem' }}>01</span>
                    後台管理系統
                  </h3>
                  <p>
                    以「信眾」為核心，整合基本資料、家族成員、歷史服務紀錄與參與狀態，讓行政人員能在單一介面完成查詢、核對與後續追蹤任務，減少對紙本與資深人員記憶的依賴。
                  </p>
                </div>

                {/* Large Desktop Monitor Frame Shell */}
                <div
                  style={{
                    width: '100%',
                    margin: '2rem 0',
                    borderRadius: '16px',
                    border: '1px solid #cbd5e1',
                    overflow: 'hidden',
                    backgroundColor: '#1e293b',
                    boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.15)',
                  }}
                >
                  {/* Desktop Window Title Bar */}
                  <div
                    style={{
                      height: '40px',
                      backgroundColor: '#0f172a',
                      borderBottom: '1px solid #334155',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 1rem',
                      gap: '8px',
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: '#ef4444',
                      }}
                    />
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: '#f59e0b',
                      }}
                    />
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: '#10b981',
                      }}
                    />
                  </div>

                  {/* Monitor Display Content Image */}
                  <div style={{ width: '100%', backgroundColor: '#ffffff', overflow: 'hidden' }}>
                    <img
                      src="/images/backend management.jpg"
                      alt="Back-end Data Management System Monitor View"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ACTION FLOW SHOWCASE — Full Bleed Light Grey Section */}
            <div
              className="temple-section temple-fade-in"
              style={{
                width: '100vw',
                marginLeft: 'calc(-50vw + 50%)',
                backgroundColor: '#f1f5f9',
                padding: '6rem clamp(1.5rem, 5vw, 4rem)',
                marginTop: '5rem',
              }}
            >
              <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5rem' }}>
                  {/* Module 02: LINE */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2.5rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    {/* Text Column (left) */}
                    <div style={{ flex: '1 1 320px', minWidth: '260px' }}>
                      <h3
                        style={{
                          margin: '0 0 0.5rem',
                          fontSize: 'clamp(1.25rem, 1.8vw, 1.45rem)',
                          fontWeight: 700,
                          color: '#0f172a',
                        }}
                      >
                        <span style={{ color: '#2563eb', marginRight: '0.5rem' }}>02</span>
                        LINE 官方帳號
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          maxWidth: '100%',
                          color: '#334155',
                          fontSize: '1.05rem',
                          lineHeight: 1.8,
                        }}
                      >
                        作為信眾存取線上服務的主要入口，串聯報名系統、身分驗證與常見問題解答，讓信眾能在熟悉的
                        LINE 介面中完成操作，同時減少現場與電話詢問負擔。
                      </p>
                    </div>

                    {/* Phone Mockups (right) — no card wrapper */}
                    <div
                      className="temple-line-mockups-wrap"
                      style={{
                        flex: '1 1 720px',
                        minWidth: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1.75rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* iPhone 16 Pro Mockup 1 */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.5rem',
                          maxWidth: 'min(320px, 46vw)',
                          width: '100%',
                          minWidth: '260px',
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '9 / 19.5',
                            borderRadius: 'clamp(24px, 4.5vw, 40px)',
                            border: '6px solid #1e293b',
                            outline: '1px solid #0f172a',
                            backgroundColor: '#000000',
                            boxShadow:
                              '0 20px 40px -10px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(15, 23, 42, 0.1)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* iPhone 16 Pro Dynamic Island */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '10px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '72px',
                              height: '20px',
                              backgroundColor: '#000000',
                              borderRadius: '16px',
                              zIndex: 20,
                              border: '1px solid #1a202c',
                            }}
                          />
                          <img
                            src="/images/temple-line-official-account-1.png"
                            alt="LINE 官方帳號介面 1"
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 'clamp(19px, 3.6vw, 32px)',
                            }}
                          />
                          {/* Bottom Home Indicator */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '110px',
                              height: '4px',
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              borderRadius: '2px',
                              zIndex: 20,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: '0.9rem',
                            color: '#475569',
                            textAlign: 'center',
                            marginTop: '0.5rem',
                          }}
                        >
                          官方帳號首頁
                        </div>
                      </div>

                      {/* Large Right Arrow Icon */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#64748b',
                          fontSize: '2.25rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        ➔
                      </div>

                      {/* iPhone 16 Pro Mockup 2 */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.5rem',
                          maxWidth: 'min(320px, 46vw)',
                          width: '100%',
                          minWidth: '260px',
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '9 / 19.5',
                            borderRadius: 'clamp(24px, 4.5vw, 40px)',
                            border: '6px solid #1e293b',
                            outline: '1px solid #0f172a',
                            backgroundColor: '#000000',
                            boxShadow:
                              '0 20px 40px -10px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(15, 23, 42, 0.1)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* iPhone 16 Pro Dynamic Island */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '10px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '72px',
                              height: '20px',
                              backgroundColor: '#000000',
                              borderRadius: '16px',
                              zIndex: 20,
                              border: '1px solid #1a202c',
                            }}
                          />
                          <img
                            src="/images/temple-line-official-account-2.png"
                            alt="LINE 官方帳號介面 2"
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 'clamp(19px, 3.6vw, 32px)',
                            }}
                          />
                          {/* Bottom Home Indicator */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '110px',
                              height: '4px',
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              borderRadius: '2px',
                              zIndex: 20,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: '0.9rem',
                            color: '#475569',
                            textAlign: 'center',
                            marginTop: '0.5rem',
                          }}
                        >
                          活動報名資訊
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Module 03: 前台線上報名 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2.5rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    {/* Text Column (left) */}
                    <div style={{ flex: '1 1 320px', minWidth: '260px' }}>
                      <h3
                        style={{
                          margin: '0 0 0.5rem',
                          fontSize: 'clamp(1.25rem, 1.8vw, 1.45rem)',
                          fontWeight: 700,
                          color: '#0f172a',
                        }}
                      >
                        <span style={{ color: '#2563eb', marginRight: '0.5rem' }}>03</span>
                        線上報名系統｜線上報名
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          maxWidth: '100%',
                          color: '#334155',
                          fontSize: '1.05rem',
                          lineHeight: 1.8,
                        }}
                      >
                        將原本需現場排隊、人工核對與紙本填表的報名流程，轉化為可自助完成的線上操作，透過清楚的步驟與狀態回饋串聯後續後台行政作業。
                      </p>
                    </div>

                    {/* Phone Mockups (right) — no card wrapper */}
                    <div
                      className="temple-line-mockups-wrap"
                      style={{
                        flex: '1 1 720px',
                        minWidth: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1.75rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* iPhone 16 Pro Mockup 1 */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.5rem',
                          maxWidth: 'min(320px, 46vw)',
                          width: '100%',
                          minWidth: '260px',
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '9 / 19.5',
                            borderRadius: 'clamp(24px, 4.5vw, 40px)',
                            border: '6px solid #1e293b',
                            outline: '1px solid #0f172a',
                            backgroundColor: '#000000',
                            boxShadow:
                              '0 20px 40px -10px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(15, 23, 42, 0.1)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* iPhone 16 Pro Dynamic Island */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '10px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '72px',
                              height: '20px',
                              backgroundColor: '#000000',
                              borderRadius: '16px',
                              zIndex: 20,
                              border: '1px solid #1a202c',
                            }}
                          />
                          <img
                            src="/images/temple-registration-flow-1.png"
                            alt="線上報名流程 1"
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 'clamp(19px, 3.6vw, 32px)',
                            }}
                          />
                          {/* Bottom Home Indicator */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '110px',
                              height: '4px',
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              borderRadius: '2px',
                              zIndex: 20,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: '0.9rem',
                            color: '#475569',
                            textAlign: 'center',
                            marginTop: '0.5rem',
                          }}
                        >
                          報名表單
                        </div>
                      </div>

                      {/* Large Right Arrow Icon */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#64748b',
                          fontSize: '2.25rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        ➔
                      </div>

                      {/* iPhone 16 Pro Mockup 2 */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.5rem',
                          maxWidth: 'min(320px, 46vw)',
                          width: '100%',
                          minWidth: '260px',
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '9 / 19.5',
                            borderRadius: 'clamp(24px, 4.5vw, 40px)',
                            border: '6px solid #1e293b',
                            outline: '1px solid #0f172a',
                            backgroundColor: '#000000',
                            boxShadow:
                              '0 20px 40px -10px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(15, 23, 42, 0.1)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* iPhone 16 Pro Dynamic Island */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '10px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '72px',
                              height: '20px',
                              backgroundColor: '#000000',
                              borderRadius: '16px',
                              zIndex: 20,
                              border: '1px solid #1a202c',
                            }}
                          />
                          <img
                            src="/images/temple-registration-flow-2.png"
                            alt="線上報名流程 2"
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 'clamp(19px, 3.6vw, 32px)',
                            }}
                          />
                          {/* Bottom Home Indicator */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '110px',
                              height: '4px',
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              borderRadius: '2px',
                              zIndex: 20,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: '0.9rem',
                            color: '#475569',
                            textAlign: 'center',
                            marginTop: '0.5rem',
                          }}
                        >
                          匯款資訊
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Module 04: 查詢與歷史紀錄 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2.5rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    {/* Text Column (left) */}
                    <div style={{ flex: '1 1 320px', minWidth: '260px' }}>
                      <h3
                        style={{
                          margin: '0 0 0.5rem',
                          fontSize: 'clamp(1.25rem, 1.8vw, 1.45rem)',
                          fontWeight: 700,
                          color: '#0f172a',
                        }}
                      >
                        <span style={{ color: '#2563eb', marginRight: '0.5rem' }}>04</span>
                        線上報名系統｜查詢歷史紀錄
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          maxWidth: '100%',
                          color: '#334155',
                          fontSize: '1.05rem',
                          lineHeight: 1.8,
                        }}
                      >
                        提供信眾與行政人員完整的服務紀錄查詢，隨時掌握報名狀態、歷史辦理項目與後續流程進度。
                      </p>
                    </div>

                    {/* Phone Mockups (right) — no card wrapper */}
                    <div
                      className="temple-line-mockups-wrap"
                      style={{
                        flex: '1 1 720px',
                        minWidth: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1.75rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* iPhone 16 Pro Mockup 1 */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.5rem',
                          maxWidth: 'min(320px, 46vw)',
                          width: '100%',
                          minWidth: '260px',
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '9 / 19.5',
                            borderRadius: 'clamp(24px, 4.5vw, 40px)',
                            border: '6px solid #1e293b',
                            outline: '1px solid #0f172a',
                            backgroundColor: '#000000',
                            boxShadow:
                              '0 20px 40px -10px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(15, 23, 42, 0.1)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* iPhone 16 Pro Dynamic Island */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '10px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '72px',
                              height: '20px',
                              backgroundColor: '#000000',
                              borderRadius: '16px',
                              zIndex: 20,
                              border: '1px solid #1a202c',
                            }}
                          />
                          <img
                            src="/images/temple-inquiry-history-flow-1.png"
                            alt="查詢與歷史紀錄 1"
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 'clamp(19px, 3.6vw, 32px)',
                            }}
                          />
                          {/* Bottom Home Indicator */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '110px',
                              height: '4px',
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              borderRadius: '2px',
                              zIndex: 20,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: '0.9rem',
                            color: '#475569',
                            textAlign: 'center',
                            marginTop: '0.5rem',
                          }}
                        >
                          訂單紀錄
                        </div>
                      </div>

                      {/* Large Right Arrow Icon */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#64748b',
                          fontSize: '2.25rem',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        ➔
                      </div>

                      {/* iPhone 16 Pro Mockup 2 */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.5rem',
                          maxWidth: 'min(320px, 46vw)',
                          width: '100%',
                          minWidth: '260px',
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '9 / 19.5',
                            borderRadius: 'clamp(24px, 4.5vw, 40px)',
                            border: '6px solid #1e293b',
                            outline: '1px solid #0f172a',
                            backgroundColor: '#000000',
                            boxShadow:
                              '0 20px 40px -10px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(15, 23, 42, 0.1)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* iPhone 16 Pro Dynamic Island */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '10px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '72px',
                              height: '20px',
                              backgroundColor: '#000000',
                              borderRadius: '16px',
                              zIndex: 20,
                              border: '1px solid #1a202c',
                            }}
                          />
                          <img
                            src="/images/temple-inquiry-history-flow-2.png"
                            alt="查詢與歷史紀錄 2"
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 'clamp(19px, 3.6vw, 32px)',
                            }}
                          />
                          {/* Bottom Home Indicator */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '110px',
                              height: '4px',
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              borderRadius: '2px',
                              zIndex: 20,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: '0.9rem',
                            color: '#475569',
                            textAlign: 'center',
                            marginTop: '0.5rem',
                          }}
                        >
                          芳名錄列表
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* IMPLEMENTATION ROADMAP */}
            <div className="temple-section-light temple-section temple-fade-in">
              <div className="temple-section-label">專案推動與落實</div>
              <h2>MVA 策略與漸進式建置藍圖</h2>
              <p>
                考量宮廟過去缺乏數位工具使用經驗，我們採用 MVA (Minimum Viable
                Architecture，最小可行架構)
                策略：先盤點完整的服務藍圖全貌，再精選對服務穩定度影響最大的核心模組優先開發，後續再依需求逐步擴充。
              </p>
              <p>
                每一次的建置導入同時也是田野驗證。我們與各角色持續確認流程細節，迅速將反饋轉化為系統調整，並將協作中的默會知識逐步轉化為系統規則，減少對特定個人記憶的依賴。
              </p>
              <p>
                隨著系統展現出顯著成效，其他部門也主動提出擴充更多管理功能的需求。本系統從一次性的專案成果，演進為可持續發展與擴充的組織數位資產。
              </p>

              <div className="temple-roadmap">
                <div className="temple-roadmap-item">
                  <div className="temple-roadmap-dot phase1">01</div>
                  <div className="temple-roadmap-content">
                    <h4>建構完整系統架構藍圖</h4>
                    <p>
                      盤點跨部門的全流程服務藍圖，記錄每個流程、角色與接觸點，建立完整的建置全貌。
                    </p>
                    <span className="temple-roadmap-tag core">基礎架構</span>
                  </div>
                </div>
                <div className="temple-roadmap-item">
                  <div className="temple-roadmap-dot phase2">02</div>
                  <div className="temple-roadmap-content">
                    <h4>MVA 核心模組建置</h4>
                    <p>
                      精選並開發最關鍵的四大模組：信眾管理、活動/科儀報名管理、財務對帳管理與歷史紀錄管理——達成服務穩定所需的最小模組集合。
                    </p>
                    <span className="temple-roadmap-tag core">核心交付</span>
                  </div>
                </div>
                <div className="temple-roadmap-item">
                  <div className="temple-roadmap-dot phase3">03</div>
                  <div className="temple-roadmap-content">
                    <h4>後續漸進擴充</h4>
                    <p>
                      依需求與緊急度，延伸擴充至更多模組：其他科儀模組（點燈、普渡、誦經）、科儀文疏、標籤與現場作業，以及
                      LINE 主動通知與查詢。
                    </p>
                    <span className="temple-roadmap-tag expand">未來擴充</span>
                  </div>
                </div>
              </div>
            </div>

            {/* IMPACT — Full-bleed dark */}
            <div className="temple-fullbleed-dark temple-section temple-fade-in">
              <h2 style={{ textAlign: 'center', marginBottom: '1.25rem' }}>專案成果</h2>
              <p
                style={{
                  textAlign: 'center',
                  maxWidth: '800px',
                  margin: '0 auto 3rem',
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                }}
              >
                本專案以 <strong style={{ color: '#d4a017' }}>「數位服務替代率」</strong>{' '}
                作為北極星指標，衡量原由現場人工處理的科儀報名、對帳與通知流程，轉由線上平台自動化完成的比例。
              </p>

              {/* Minimalist Key Metric Section */}
              <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
                <div
                  style={{
                    fontSize: 'clamp(3.5rem, 8vw, 5.5rem)',
                    fontWeight: 800,
                    color: '#ffffff',
                    lineHeight: 1,
                  }}
                >
                  52.2
                  <span style={{ fontSize: '2.5rem', color: '#d4a017', marginLeft: '0.2rem' }}>
                    %
                  </span>
                </div>
                <div
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: '#d4a017',
                    marginTop: '0.5rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  北極星指標 ｜ 數位服務替代率 (Digital Service Replacement Rate)
                </div>
                <p
                  style={{
                    fontSize: '0.95rem',
                    color: 'rgba(255,255,255,0.6)',
                    marginTop: '0.75rem',
                    maxWidth: '650px',
                    margin: '0.75rem auto 0',
                    lineHeight: 1.6,
                  }}
                >
                  計算公式：線上服務完成數 ÷ 總服務申請數 × 100%
                  <br />
                  （導入前線上完成率為 0%，系統上線後成功將半數以上服務量轉移至數位端）
                </p>
              </div>

              {/* Minimalist 3-Column Metrics (Flat Layout) */}
              <div
                className="temple-impact-grid"
                style={{
                  maxWidth: '1180px',
                  margin: '0 auto 4rem',
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  padding: '2.5rem 0',
                }}
              >
                <div style={{ textAlign: 'center', padding: '0 1rem' }}>
                  <div
                    style={{
                      fontSize: '2.5rem',
                      fontWeight: 800,
                      color: '#ffffff',
                      lineHeight: 1,
                      marginBottom: '0.5rem',
                    }}
                  >
                    9,200+
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#ffffff' }}>
                    LINE 官方帳號好友數
                  </div>
                  <div
                    style={{
                      fontSize: '0.875rem',
                      color: 'rgba(255,255,255,0.5)',
                      marginTop: '0.35rem',
                    }}
                  >
                    成為最主要資訊發布管道
                  </div>
                </div>
                <div style={{ textAlign: 'center', padding: '0 1rem' }}>
                  <div
                    style={{
                      fontSize: '2.5rem',
                      fontWeight: 800,
                      color: '#ffffff',
                      lineHeight: 1,
                      marginBottom: '0.5rem',
                    }}
                  >
                    6,100+
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#ffffff' }}>
                    線上完成報名訂單
                  </div>
                  <div
                    style={{
                      fontSize: '0.875rem',
                      color: 'rgba(255,255,255,0.5)',
                      marginTop: '0.35rem',
                    }}
                  >
                    信眾自主線上辦理對帳
                  </div>
                </div>
                <div style={{ textAlign: 'center', padding: '0 1rem' }}>
                  <div
                    style={{
                      fontSize: '2.5rem',
                      fontWeight: 800,
                      color: '#ffffff',
                      lineHeight: 1,
                      marginBottom: '0.5rem',
                    }}
                  >
                    18 → 6
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#ffffff' }}>
                    人工處理節點整合
                  </div>
                  <div
                    style={{
                      fontSize: '0.875rem',
                      color: 'rgba(255,255,255,0.5)',
                      marginTop: '0.35rem',
                    }}
                  >
                    觸點數量減少 66.7%
                  </div>
                </div>
                <div style={{ textAlign: 'center', padding: '0 1rem' }}>
                  <div
                    style={{
                      fontSize: '2.5rem',
                      fontWeight: 800,
                      color: '#34d399',
                      lineHeight: 1,
                      marginBottom: '0.5rem',
                    }}
                  >
                    4.4
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#ffffff' }}>
                    系統滿意度 (滿分 5)
                  </div>
                  <div
                    style={{
                      fontSize: '0.875rem',
                      color: 'rgba(255,255,255,0.5)',
                      marginTop: '0.35rem',
                    }}
                  >
                    高於舊有流程 4.0 分
                  </div>
                </div>
              </div>

              {/* Minimalist Time Savings List */}
              <div style={{ maxWidth: '850px', margin: '0 auto' }}>
                <h3
                  style={{
                    textAlign: 'center',
                    marginBottom: '2rem',
                    fontSize: '1.35rem',
                    fontWeight: 700,
                    color: '#ffffff',
                  }}
                >
                  作業處理時間變化
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div
                    className="temple-time-change-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1.15rem 1.5rem',
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      borderRadius: '12px',
                      borderLeft: '3px solid #d4a017',
                    }}
                  >
                    <div>
                      <div style={{ color: '#ffffff', fontWeight: 700, fontSize: '1.05rem' }}>
                        專職行政人員 ｜ 單場活動資料整理
                      </div>
                      <div
                        style={{
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: '0.875rem',
                          marginTop: '0.2rem',
                        }}
                      >
                        人工核對轉化為系統自動統計
                      </div>
                    </div>
                    <div className="time-val" style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span
                        style={{
                          color: 'rgba(255,255,255,0.4)',
                          textDecoration: 'line-through',
                          fontSize: '0.9rem',
                          marginRight: '0.75rem',
                        }}
                      >
                        原先：數天
                      </span>
                      <span style={{ color: '#34d399', fontWeight: 800, fontSize: '1.2rem' }}>
                        2–3 小時
                      </span>
                    </div>
                  </div>

                  <div
                    className="temple-time-change-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1.15rem 1.5rem',
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      borderRadius: '12px',
                      borderLeft: '3px solid #d4a017',
                    }}
                  >
                    <div>
                      <div style={{ color: '#ffffff', fontWeight: 700, fontSize: '1.05rem' }}>
                        現場服務志工 ｜ 單筆資料登錄與對帳
                      </div>
                      <div
                        style={{
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: '0.875rem',
                          marginTop: '0.2rem',
                        }}
                      >
                        信眾先填完，志工僅做單純核對
                      </div>
                    </div>
                    <div className="time-val" style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span
                        style={{
                          color: 'rgba(255,255,255,0.4)',
                          textDecoration: 'line-through',
                          fontSize: '0.9rem',
                          marginRight: '0.75rem',
                        }}
                      >
                        原先：數天
                      </span>
                      <span style={{ color: '#34d399', fontWeight: 800, fontSize: '1.2rem' }}>
                        1–2 分鐘
                      </span>
                    </div>
                  </div>

                  <div
                    className="temple-time-change-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1.15rem 1.5rem',
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      borderRadius: '12px',
                      borderLeft: '3px solid #d4a017',
                    }}
                  >
                    <div>
                      <div style={{ color: '#ffffff', fontWeight: 700, fontSize: '1.05rem' }}>
                        參拜信眾 ｜ 線上自主報名與確認
                      </div>
                      <div
                        style={{
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: '0.875rem',
                          marginTop: '0.2rem',
                        }}
                      >
                        不用現場排隊，隨時隨地線上完成
                      </div>
                    </div>
                    <div className="time-val" style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span
                        style={{
                          color: 'rgba(255,255,255,0.4)',
                          textDecoration: 'line-through',
                          fontSize: '0.9rem',
                          marginRight: '0.75rem',
                        }}
                      >
                        原先：數天
                      </span>
                      <span style={{ color: '#34d399', fontWeight: 800, fontSize: '1.2rem' }}>
                        1–2 分鐘
                      </span>
                    </div>
                  </div>
                </div>

                {/* Elegant Subtle Divider between Impact Metrics and Field Voices */}
                <div
                  style={{
                    maxWidth: '850px',
                    margin: '5rem auto 4.5rem',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                  }}
                />

                {/* TESTIMONIALS — Integrated inside same Full-bleed dark container */}
                <div>
                  <h2 style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
                    來自田野第一線的聲音
                  </h2>
                  <p style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 2.5rem' }}>
                    來自三大利害關係人角色的真實反饋，最能體現系統帶來的轉變：
                  </p>

                  <div className="temple-testimonials">
                    <div className="temple-testimonial">
                      <div className="temple-testimonial-role">專職人員</div>
                      <p className="temple-testimonial-text">
                        「現在名單、繳費跟狀態都在系統裡，清楚又令人安心——我們不用再整天擔心漏掉東西了。」
                      </p>
                    </div>
                    <div className="temple-testimonial">
                      <div className="temple-testimonial-role">服務志工</div>
                      <p className="temple-testimonial-text">
                        「以前我很擔心幫信眾填錯資料。現在他們自己先填好，我們只需要做核對確認就可以了。」
                      </p>
                    </div>
                    <div className="temple-testimonial">
                      <div className="temple-testimonial-role">信眾</div>
                      <p className="temple-testimonial-text">
                        「以前報名科儀都不確定到底有沒有成功——有時候有報上、有時候沒有。現在整個流程清楚多了。」
                      </p>
                    </div>
                  </div>

                  <p style={{ textAlign: 'center', maxWidth: '700px', margin: '2.5rem auto 0' }}>
                    系統穩定運行後，原本持觀望態度的其他部門主動提出擴充需求，希望依循相同的後台管理模式整理自家的數據與工作流程。本專案從一次性的服務優化任務，演進為宮廟內部持續擴充的數位基礎設施。
                  </p>
                </div>
              </div>
            </div>

            {/* REAL ON-SITE PHOTO */}
            <div className="temple-img-diagram">
              <img
                src="/images/real work.png"
                alt="Real on-site photo after service digitization"
              />
            </div>
            <p
              className="temple-img-caption"
              style={{ marginTop: '0.75rem', marginBottom: '3rem' }}
            >
              服務數位化後之現場真實運作照片
            </p>

            {/* REFLECTION */}
            <div className="temple-section-light temple-section temple-fade-in">
              <div className="temple-section-label">專案省思</div>
              <h2>核心學習與收穫</h2>
              <p>
                本專案訓練我在高度複雜、無現成框架可循的環境中，從定義問題一路推進至系統落地部署。具體展現了三項能力：
              </p>

              <div className="temple-reflection-list">
                <div className="temple-reflection-item">
                  <div className="temple-reflection-num">01</div>
                  <p>
                    在複雜的服務環境中執行完整的 UX Research，並將訪談洞察直接轉化為系統功能優先級
                  </p>
                </div>
                <div className="temple-reflection-item">
                  <div className="temple-reflection-num">02</div>
                  <p>
                    在文化脈絡與效率需求之間進行權衡，將服務設計從研究方法論推進至真實運作的數位系統
                  </p>
                </div>
                <div className="temple-reflection-item">
                  <div className="temple-reflection-num">03</div>
                  <p>
                    協調管理層、執行者、志工、使用者與工程團隊，並善用 AI
                    輔助研究、設計與開發溝通，提升小團隊的執行效率
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="temple-section-light temple-section temple-fade-in">
              <div className="temple-cta-wrap">
                <a
                  href="https://medium.com/ditl/%E7%95%B6%E4%BF%A1%E4%BB%B0%E9%81%87%E8%A6%8B%E7%A7%91%E6%8A%80-%E8%87%BA%E7%81%A3%E9%81%93%E6%95%99%E5%AE%AE%E5%BB%9F%E6%9C%8D%E5%8B%99%E7%9A%84%E6%95%B8%E4%BD%8D%E8%BD%89%E5%9E%8B%E5%AF%A6%E8%B8%90-6ff07126d021"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: '#1f2937',
                    color: '#ffffff',
                    padding: '1rem 2rem',
                    borderRadius: '999px',
                    fontWeight: 500,
                    fontSize: '1.05rem',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#000000'
                    e.currentTarget.style.transform = 'translateY(-2px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#1f2937'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  想了解更多研究細節與產品設計內容嗎？歡迎閱讀 Medium 完整文章{' '}
                  <span style={{ marginLeft: '0.25rem' }}>→</span>
                </a>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '0 0 4rem' }}>
              <button
                onClick={() => router.push('/#work')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  backgroundColor: '#ffffff',
                  color: '#0f172a',
                  border: 'none',
                  padding: '0.9rem 2.25rem',
                  borderRadius: '9999px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 10px 25px rgba(255, 255, 255, 0.15)',
                  transition: 'all 0.2s ease',
                }}
              >
                <ArrowLeft size={18} />
                返回作品列表
              </button>
            </div>
          </div>
        )
      } else if (isHealingHerbar) {
        return (
          /* ====== REDESIGNED: Healing Herbar · Brand Research ====== */
          <div className="hh-case-root">
            <style>{`
              .hh-section { margin-bottom: 5rem; }
              .hh-section:last-child { margin-bottom: 0; }
              .hh-fullbleed { width: 100vw; margin-left: calc(-50vw + 50%); padding: 4.5rem clamp(1.25rem, 4vw, 2.5rem); position: relative; overflow: hidden; }
              .hh-fullbleed-dark { width: 100vw; margin-left: calc(-50vw + 50%); background: #181D1A; padding: 4.5rem clamp(1.25rem, 4vw, 2.5rem); position: relative; overflow: hidden; }
              .hh-fullbleed-warm { width: 100vw; margin-left: calc(-50vw + 50%); background: #F7F5F0; padding: 4.5rem clamp(1.25rem, 4vw, 2.5rem); position: relative; overflow: hidden; }
              .hh-inner, .hh-inner-narrow { max-width: 1200px; margin: 0 auto; padding: 0 clamp(1.25rem, 4vw, 2.5rem); }
              .hh-inner + .hh-fullbleed-dark,
              .hh-inner + .hh-fullbleed-warm,
              .hh-inner-narrow + .hh-fullbleed-dark,
              .hh-inner-narrow + .hh-fullbleed-warm { margin-top: 5rem; }
              .hh-fullbleed-dark + .hh-inner,
              .hh-fullbleed-dark + .hh-inner-narrow,
              .hh-fullbleed-warm + .hh-inner,
              .hh-fullbleed-warm + .hh-inner-narrow { margin-top: 5rem; }

              .hh-eyebrow { font-size: 0.85rem; font-weight: 700; color: #2D6A4F; text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 0.75rem; display: inline-flex; align-items: center; }
              .hh-eyebrow-light { font-size: 0.85rem; font-weight: 700; color: #52B788; text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 0.75rem; display: inline-flex; align-items: center; }

              .hh-section-title { font-size: clamp(1.65rem, 2.5vw, 2.15rem); font-weight: 700; line-height: 1.25; color: #111827; margin: 0 0 1.25rem 0; letter-spacing: -0.01em; }
              .hh-section-title-light { font-size: clamp(1.65rem, 2.5vw, 2.15rem); font-weight: 700; line-height: 1.25; color: #FFFFFF; margin: 0 0 1.25rem 0; letter-spacing: -0.01em; }
              .hh-body { font-size: 1.125rem; line-height: 1.8; color: #4B5563; margin: 0 0 1.25rem 0; }
              .hh-body-light { font-size: 1.125rem; line-height: 1.8; color: #D1D5DB; margin: 0 0 1.25rem 0; }

              /* Hero */
              .hh-hero { position: relative; width: 100%; min-height: 75vh; display: flex; flex-direction: column; justify-content: center; overflow: hidden; }
              .hh-hero-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; z-index: 0; filter: brightness(0.95); }
              .hh-hero-overlay { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(15, 23, 20, 0.88) 0%, rgba(27, 67, 50, 0.78) 55%, rgba(15, 23, 20, 0.45) 100%); z-index: 1; }
              .hh-hero-gradient-bottom { position: absolute; bottom: 0; left: 0; right: 0; height: 140px; background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%); z-index: 3; pointer-events: none; }
              .hh-hero-content { position: relative; z-index: 2; max-width: 1400px; margin: 0 auto; padding: 7rem clamp(1.25rem, 4vw, 2.5rem); width: 100%; text-align: center; }
              .hh-hero-eyebrow { font-size: 0.85rem; font-weight: 700; color: #74C69D; text-transform: uppercase; letter-spacing: 0.1em; background-color: rgba(116, 198, 157, 0.15); padding: 6px 16px; border-radius: 99px; display: inline-block; margin-bottom: 1.25rem; }
              .hh-hero-title { font-size: clamp(2.5rem, 4.5vw, 3.6rem); font-weight: 800; line-height: 1.15; color: #ffffff; margin: 0 auto 1.25rem; max-width: 1200px; letter-spacing: -0.02em; }
              .hh-hero-sub { font-size: clamp(1.15rem, 1.8vw, 1.35rem); line-height: 1.7; color: rgba(255,255,255,0.88); max-width: 1000px; margin: 0 auto; font-weight: 400; }

              /* Key Stats Metrics Bar */
              .hh-metrics-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin: -2.5rem 0 3.5rem 0; position: relative; z-index: 4; }
              .hh-metric-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.25rem 1rem; text-align: center; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01); transition: transform 0.2s ease, box-shadow 0.2s ease; }
              .hh-metric-card:hover { transform: translateY(-3px); box-shadow: 0 14px 28px -5px rgba(45, 106, 79, 0.12); }
              .hh-metric-num { font-size: 2rem; font-weight: 800; color: #2D6A4F; line-height: 1.1; margin-bottom: 0.25rem; }
              .hh-metric-label { font-size: 0.85rem; font-weight: 700; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; }

              /* Snapshot Integrated in Hero */
              .hh-snapshot { display: flex; flex-wrap: wrap; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.15); backdrop-filter: blur(12px); border-radius: 16px; padding: 1.5rem 2rem; margin-top: 2.5rem; gap: 1.5rem; text-align: left; max-width: 1160px; margin-left: auto; margin-right: auto; }
              .hh-snapshot-item { flex: 1; min-width: 150px; }
              .hh-snapshot-label { font-size: 0.78rem; font-weight: 700; color: #74C69D; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 0.35rem; }
              .hh-snapshot-value { font-size: 1.05rem; font-weight: 600; color: #FFFFFF; line-height: 1.4; }
              .hh-snapshot-value-sm { font-size: 0.95rem; font-weight: 500; color: rgba(255, 255, 255, 0.85); line-height: 1.55; }

              /* Context Cards */
              .hh-context-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin: 2rem 0; }
              .hh-context-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.75rem; box-shadow: 0 2px 8px rgba(0,0,0,0.03); }
              .hh-context-card.hh-tradition { border-top: 4px solid #D97706; }
              .hh-context-card.hh-modern { border-top: 4px solid #2D6A4F; }
              .hh-context-card h4 { font-size: 0.95rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; margin: 0 0 1.25rem 0; display: flex; align-items: center; justify-content: space-between; }
              .hh-context-card.hh-tradition h4 { color: #D97706; }
              .hh-context-card.hh-modern h4 { color: #2D6A4F; }
              .hh-context-card ul { margin: 0; padding: 0; list-style: none; }
              .hh-context-card li { font-size: 1.05rem; line-height: 1.65; color: #374151; padding: 0.5rem 0 0.5rem 1.25rem; border-bottom: 1px dashed #F3F4F6; position: relative; }
              .hh-context-card li::before { content: "•"; position: absolute; left: 0.25rem; color: #2D6A4F; font-weight: bold; }
              .hh-context-card.hh-tradition li::before { color: #D97706; }
              .hh-context-card li:last-child { border-bottom: none; }

              /* Key Highlight Statement Box */
              .hh-statement-box { background: linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%); border-left: 4px solid #2D6A4F; border-radius: 0 12px 12px 0; padding: 1.75rem 2.25rem; margin: 2.5rem 0 0 0; }
              .hh-statement-text { font-size: clamp(1.15rem, 2.2vw, 1.4rem); font-weight: 600; line-height: 1.6; color: #1B4332; margin: 0; }

              /* Research Challenge Dark Cards */
              .hh-challenge-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin: 2rem 0; }
              .hh-challenge-card { background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 12px; padding: 1.75rem; backdrop-filter: blur(8px); }
              .hh-challenge-card h4 { font-size: 1.2rem; font-weight: 700; color: #74C69D; margin: 0 0 0.75rem 0; }
              .hh-challenge-card p { font-size: 1.05rem; line-height: 1.7; color: #D1D5DB; margin: 0; }

              /* Approach 4-step Cards */
              .hh-approach-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.25rem; margin: 2rem 0; }
              .hh-approach-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.5rem; transition: transform 0.2s ease, box-shadow 0.2s ease; display: flex; flex-direction: column; }
              .hh-approach-card:hover { transform: translateY(-3px); box-shadow: 0 10px 20px -5px rgba(0,0,0,0.06); }
              .hh-approach-num { font-size: 1.75rem; font-weight: 800; color: #2D6A4F; background: #E8F5E9; width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem; }
              .hh-approach-title { font-size: 1.1rem; font-weight: 700; color: #111827; margin-bottom: 0.5rem; line-height: 1.3; }
              .hh-approach-desc { font-size: 0.95rem; line-height: 1.6; color: #6B7280; margin: 0; flex: 1; }

              /* Field Journey Steps */
              .hh-field-journey { display: grid; grid-template-columns: repeat(6, 1fr); gap: 0.75rem; margin: 2rem 0; }
              .hh-field-step { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 10px; padding: 1rem 0.75rem; text-align: center; position: relative; }
              .hh-field-step-num { font-size: 0.75rem; font-weight: 800; color: #2D6A4F; text-transform: uppercase; letter-spacing: 0.1em; background: #E8F5E9; padding: 0.15rem 0.4rem; border-radius: 4px; display: inline-block; margin-bottom: 0.5rem; }
              .hh-field-step-label { font-size: 0.95rem; font-weight: 700; color: #111827; margin-bottom: 0.35rem; }
              .hh-field-step-desc { font-size: 0.85rem; color: #6B7280; line-height: 1.4; }

              /* 4 Layers Grid */
              .hh-field-layers { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; margin: 2rem 0; }
              .hh-field-layer { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.5rem; box-shadow: 0 2px 6px rgba(0,0,0,0.02); }
              .hh-field-layer h5 { font-size: 1.1rem; font-weight: 700; color: #1B4332; margin: 0 0 1rem 0; display: flex; align-items: center; gap: 0.5rem; }
              .hh-field-layer h5::before { content: ""; width: 6px; height: 18px; background: #2D6A4F; border-radius: 3px; }
              .hh-field-layer ul { margin: 0; padding: 0; list-style: none; }
              .hh-field-layer li { font-size: 1.02rem; line-height: 1.6; color: #4B5563; padding: 0.4rem 0; padding-left: 1.25rem; position: relative; }
              .hh-field-layer li::before { content: "•"; position: absolute; left: 0.25rem; color: #2D6A4F; font-weight: bold; }

              /* Image Grid */
              .hh-img-grid-4 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; margin-top: 2rem; }
              .hh-img-grid-4 img { width: 100%; height: 300px; object-fit: cover; border-radius: 12px; border: 1px solid #E5E7EB; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }

              /* Research Perspectives Cards */
              .hh-research-perspectives { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1.25rem; margin: 2rem 0; }
              .hh-perspective { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.75rem; display: flex; flex-direction: column; box-shadow: 0 2px 8px rgba(0,0,0,0.03); }
              .hh-persp-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
              .hh-persp-badge { font-size: 0.78rem; font-weight: 800; color: #2D6A4F; background: #E8F5E9; padding: 0.25rem 0.65rem; border-radius: 20px; }
              .hh-perspective h5 { font-size: 1.2rem; font-weight: 700; color: #111827; margin: 0 0 0.5rem 0; }
              .hh-perspective p { font-size: 0.95rem; color: #6B7280; line-height: 1.55; margin: 0 0 1rem 0; }
              .hh-perspective ul { margin: 0; padding: 0; list-style: none; border-top: 1px solid #F3F4F6; padding-top: 0.75rem; }
              .hh-perspective li { font-size: 0.95rem; line-height: 1.55; color: #374151; padding: 0.35rem 0; display: flex; align-items: center; gap: 0.4rem; }
              .hh-perspective li::before { content: "✓"; color: #2D6A4F; font-size: 0.85rem; font-weight: bold; }

              /* Persona Matrix */
              .hh-matrix-wrap { width: 100%; margin: 2rem 0; }
              .hh-matrix { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
              .hh-matrix-cell { padding: 1.75rem; border-radius: 12px; display: flex; flex-direction: column; justify-content: space-between; transition: transform 0.2s ease; }
              .hh-matrix-cell.hh-core { background: #FFFFFF; border: 2px solid #2D6A4F; box-shadow: 0 8px 20px -4px rgba(45, 106, 79, 0.12); }
              .hh-matrix-cell.hh-secondary { background: #FAF9F5; border: 1px solid #E5E5E0; }
              .hh-matrix-name { font-size: 1.2rem; font-weight: 700; color: #111827; margin-bottom: 0.5rem; }
              .hh-matrix-name.hh-muted { color: #6B7280; }
              .hh-matrix-desc { font-size: 1.02rem; line-height: 1.65; color: #4B5563; margin-bottom: 1.25rem; }
              .hh-matrix-desc.hh-muted { color: #9CA3AF; }
              .hh-matrix-tags { display: flex; gap: 0.5rem; flex-wrap: wrap; }
              .hh-matrix-tag { font-size: 0.82rem; padding: 0.3rem 0.7rem; border-radius: 6px; font-weight: 600; background: #E8F5E9; color: #2D6A4F; }
              .hh-matrix-tag.hh-muted { background: #E5E7EB; color: #6B7280; }
              .hh-matrix-core-badge { display: inline-block; font-size: 0.72rem; font-weight: 800; color: #FFFFFF; background: #2D6A4F; padding: 0.25rem 0.65rem; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.75rem; width: fit-content; }

              /* Shift Grid (Before - Finding - Shift) */
              .hh-shift-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1.25rem; margin: 2.5rem 0; }
              .hh-shift-card { background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 12px; padding: 1.75rem; }
              .hh-shift-badge { font-size: 0.78rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; padding: 0.25rem 0.65rem; border-radius: 4px; display: inline-block; margin-bottom: 1rem; }
              .hh-shift-card.before .hh-shift-badge { background: rgba(239, 68, 68, 0.2); color: #FCA5A5; }
              .hh-shift-card.finding .hh-shift-badge { background: rgba(245, 158, 11, 0.2); color: #FDE68A; }
              .hh-shift-card.shift .hh-shift-badge { background: rgba(82, 183, 136, 0.25); color: #74C69D; }
              .hh-shift-title { font-size: 1.2rem; font-weight: 700; color: #FFFFFF; margin-bottom: 0.75rem; line-height: 1.35; }
              .hh-shift-text { font-size: 1.02rem; line-height: 1.65; color: #9CA3AF; }

              /* HMW Box */
              .hh-hmw-box { background: linear-gradient(135deg, rgba(45, 106, 79, 0.2) 0%, rgba(24, 29, 26, 0.9) 100%); border: 1px solid #52B788; border-radius: 16px; padding: 2.5rem 2rem; text-align: center; margin: 3rem 0 1rem 0; }
              .hh-hmw-title { font-size: clamp(1.3rem, 2.4vw, 1.7rem); font-weight: 600; line-height: 1.55; color: #FFFFFF; margin: 0; }

              /* Strategic Insights Visual Cards */
              .hh-insight-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 14px; padding: 2rem; margin-bottom: 2rem; box-shadow: 0 4px 15px rgba(0,0,0,0.03); }
              .hh-insight-card:last-child { margin-bottom: 0; }
              .hh-insight-tag { font-size: 0.82rem; font-weight: 800; color: #2D6A4F; background: #E8F5E9; padding: 0.25rem 0.75rem; border-radius: 20px; display: inline-block; margin-bottom: 0.75rem; text-transform: uppercase; letter-spacing: 0.08em; }
              .hh-insight-title { font-size: clamp(1.45rem, 2.3vw, 1.85rem); font-weight: 700; color: #111827; margin: 0 0 1.25rem 0; line-height: 1.3; }
              .hh-insight-content { display: grid; grid-template-columns: 1fr; gap: 1.25rem; }
              .hh-insight-p { font-size: 1.08rem; line-height: 1.75; color: #4B5563; margin: 0; }
              .hh-insight-impl-box { background: #F0FDF4; border-left: 4px solid #2D6A4F; border-radius: 0 10px 10px 0; padding: 1.25rem 1.5rem; margin-top: 0.5rem; }
              .hh-insight-impl-label { font-size: 0.78rem; font-weight: 800; color: #2D6A4F; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 0.35rem; }
              .hh-insight-impl-text { font-size: 1.05rem; font-weight: 600; color: #1B4332; margin: 0; line-height: 1.55; }

              /* Synthesis Ring / Chain */
              .hh-synthesis-chain { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin: 2rem 0; }
              .hh-synthesis-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.5rem 1.25rem; text-align: center; box-shadow: 0 2px 8px rgba(0,0,0,0.03); }
              .hh-synthesis-step { font-size: 0.78rem; font-weight: 800; color: #2D6A4F; text-transform: uppercase; letter-spacing: 0.1em; background: #E8F5E9; padding: 0.2rem 0.5rem; border-radius: 4px; display: inline-block; margin-bottom: 0.75rem; }
              .hh-synthesis-card-title { font-size: 1.12rem; font-weight: 700; color: #111827; margin-bottom: 0.5rem; }
              .hh-synthesis-card-desc { font-size: 0.95rem; color: #6B7280; line-height: 1.5; margin: 0; }

              /* Strategy Cards Grid */
              .hh-strategy-map { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.25rem; margin: 2rem 0; }
              .hh-strategy-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.75rem; display: flex; flex-direction: column; justify-content: space-between; box-shadow: 0 2px 8px rgba(0,0,0,0.03); transition: transform 0.2s ease; }
              .hh-strategy-card:hover { transform: translateY(-3px); }
              .hh-strategy-num { font-size: 1.5rem; font-weight: 800; color: #2D6A4F; background: #E8F5E9; width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem; }
              .hh-strategy-card-title { font-size: 1.2rem; font-weight: 700; color: #111827; margin-bottom: 0.75rem; }
              .hh-strategy-card p { font-size: 1.02rem; line-height: 1.65; color: #4B5563; margin: 0 0 1.25rem 0; flex: 1; }
              .hh-strategy-connect { font-size: 0.82rem; font-weight: 700; color: #059669; padding-top: 0.75rem; border-top: 1px solid #F3F4F6; }

              /* Journey Opportunity Grid */
              .hh-journey-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0.75rem; margin: 2rem 0; }
              .hh-journey-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 10px; padding: 1.25rem 1rem; display: flex; flex-direction: column; justify-content: space-between; }
              .hh-journey-label { font-size: 0.82rem; font-weight: 800; color: #2D6A4F; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 0.5rem; }
              .hh-journey-desc { font-size: 0.92rem; color: #4B5563; line-height: 1.5; margin-bottom: 1rem; flex: 1; }
              .hh-journey-opp { font-size: 0.78rem; font-weight: 700; color: #059669; background: #ECFDF5; padding: 0.25rem 0.5rem; border-radius: 4px; text-align: center; }

              /* ===== STRATEGY ENGINE — Unified Framework → Execution → Opportunity Flow ===== */
              .hh-engine-inner { max-width: 1380px; }
              .hh-engine { display: grid; gap: 3.25rem; margin: 2.5rem 0 0; }
              .hh-engine-intro { text-align: center; max-width: 960px; margin: 0 auto 1rem; }
              .hh-engine-intro .hh-eyebrow { color: #74C69D; }
              .hh-engine-lead { font-size: 1.2rem; line-height: 1.9; color: rgba(255,255,255,0.85); margin: 0 auto; max-width: 860px; }
              .hh-engine-layer { position: relative; padding-top: 2.5rem; }
              .hh-engine-layer + .hh-engine-layer::before {
                content: ''; position: absolute; top: -0.1rem; left: 50%; transform: translateX(-50%);
                width: 1px; height: 2.5rem; background: linear-gradient(to bottom, rgba(82,183,136,0.15), rgba(82,183,136,0.55));
              }
              .hh-engine-layer-label { display: inline-flex; align-items: center; gap: 0.55rem; font-size: 0.85rem; font-weight: 800; color: #ffffff; text-transform: uppercase; letter-spacing: 0.16em; margin-bottom: 1.35rem; }
              .hh-engine-layer-label::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: #52B788; box-shadow: 0 0 0 4px rgba(82,183,136,0.2); }

              .hh-engine-stages { display: flex; align-items: stretch; }
              .hh-engine-stage { flex: 1; background: rgba(255,255,255,0.045); border: 1px solid rgba(255,255,255,0.12); border-radius: 14px; padding: 1.75rem 1.5rem; text-align: center; transition: transform 0.2s ease, border-color 0.2s ease; }
              .hh-engine-stage:hover { transform: translateY(-3px); border-color: rgba(82,183,136,0.45); }
              .hh-engine-stage-step { font-size: 0.8rem; font-weight: 800; color: rgba(255,255,255,0.5); letter-spacing: 0.14em; text-transform: uppercase; margin-bottom: 0.65rem; }
              .hh-engine-stage-title { font-size: 1.25rem; font-weight: 700; color: #ffffff; margin-bottom: 0.5rem; }
              .hh-engine-stage-desc { font-size: 0.98rem; color: rgba(255,255,255,0.68); line-height: 1.6; margin: 0; }
              .hh-engine-flow-arrow { display: flex; align-items: center; justify-content: center; color: #52B788; padding: 0 0.7rem; font-size: 1.4rem; font-weight: 800; flex-shrink: 0; }

              .hh-engine-strategies { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
              .hh-engine-strategy { background: linear-gradient(160deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03)); border: 1px solid rgba(255,255,255,0.14); border-radius: 16px; padding: 2rem 1.75rem; position: relative; overflow: hidden; transition: transform 0.2s ease, border-color 0.2s ease; }
              .hh-engine-strategy:hover { transform: translateY(-3px); border-color: rgba(82,183,136,0.5); }
              .hh-engine-strategy::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, #52B788, #95D5B2); }
              .hh-engine-strategy-num { font-size: 0.85rem; font-weight: 800; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.12em; margin-bottom: 0.75rem; }
              .hh-engine-strategy-title { font-size: 1.4rem; font-weight: 700; color: #ffffff; margin-bottom: 0.65rem; }
              .hh-engine-strategy-desc { font-size: 1.05rem; line-height: 1.75; color: rgba(255,255,255,0.78); margin: 0 0 1.2rem 0; }
              .hh-engine-strategy-tag { font-size: 0.82rem; font-weight: 700; color: rgba(255,255,255,0.85); background: rgba(255,255,255,0.09); border: 1px solid rgba(255,255,255,0.16); padding: 0.35rem 0.75rem; border-radius: 99px; display: inline-flex; }

              .hh-engine-opps { display: flex; align-items: stretch; }
              .hh-engine-opp { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12); border-radius: 12px; padding: 1.35rem 1.2rem; display: flex; flex-direction: column; justify-content: space-between; gap: 0.85rem; }
              .hh-engine-opp-stage { font-size: 0.85rem; font-weight: 800; color: rgba(255,255,255,0.55); text-transform: uppercase; letter-spacing: 0.1em; }
              .hh-engine-opp-desc { font-size: 0.98rem; color: rgba(255,255,255,0.78); line-height: 1.6; margin: 0; flex: 1; }
              .hh-engine-opp-tag { font-size: 0.8rem; font-weight: 700; color: rgba(255,255,255,0.85); background: rgba(255,255,255,0.09); border: 1px solid rgba(255,255,255,0.16); padding: 0.32rem 0.65rem; border-radius: 99px; align-self: flex-start; }
              .hh-engine-opp-arrow { display: flex; align-items: center; color: #52B788; padding: 0 0.5rem; font-size: 1.2rem; font-weight: 800; flex-shrink: 0; }

              @media (max-width: 1024px) {
                .hh-engine-strategies { grid-template-columns: 1fr; }
                .hh-engine-stages { flex-wrap: wrap; }
                .hh-engine-stage { flex: 1 1 45%; }
                .hh-engine-opps { flex-direction: column; }
                .hh-engine-opp-arrow { transform: rotate(90deg); padding: 0.35rem 0; }
              }
              @media (max-width: 768px) {
                .hh-engine { gap: 2.25rem; }
                .hh-engine-stages { flex-direction: column; }
                .hh-engine-flow-arrow { transform: rotate(90deg); padding: 0.35rem 0; }
                .hh-engine-strategies { grid-template-columns: 1fr; }
                .hh-engine-opps { flex-direction: column; }
                .hh-engine-opp-arrow { transform: rotate(90deg); padding: 0.35rem 0; }
              }

              /* Contribution & Reflection Grids */
              .hh-contrib-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.25rem; margin: 2rem 0; }
              .hh-contrib-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.75rem; box-shadow: 0 2px 8px rgba(0,0,0,0.03); }
              .hh-contrib-num { font-size: 1.25rem; font-weight: 800; color: #2D6A4F; background: #E8F5E9; width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem; }
              .hh-contrib-title { font-size: 1.15rem; font-weight: 700; color: #111827; margin-bottom: 0.5rem; }
              .hh-contrib-desc { font-size: 1.02rem; line-height: 1.65; color: #4B5563; margin: 0; }

              .hh-reflection-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.75rem; margin-bottom: 1.25rem; box-shadow: 0 2px 8px rgba(0,0,0,0.02); }
              .hh-reflection-card:last-child { margin-bottom: 0; }
              .hh-reflection-num { font-size: 0.82rem; font-weight: 800; color: #2D6A4F; text-transform: uppercase; letter-spacing: 0.1em; background: #E8F5E9; padding: 0.2rem 0.5rem; border-radius: 4px; display: inline-block; margin-bottom: 0.75rem; }
              .hh-reflection-title { font-size: 1.25rem; font-weight: 700; color: #111827; margin-bottom: 0.5rem; }
              .hh-reflection-text { font-size: 1.05rem; line-height: 1.7; color: #4B5563; margin: 0; }

              /* Responsive Breakpoints */
              @media (max-width: 1024px) {
                .hh-metrics-grid { grid-template-columns: repeat(2, 1fr); margin-top: 2rem; }
                .hh-approach-grid { grid-template-columns: repeat(2, 1fr); }
                .hh-synthesis-chain { grid-template-columns: repeat(2, 1fr); }
                .hh-strategy-map { grid-template-columns: 1fr; }
                .hh-contrib-grid { grid-template-columns: 1fr; }
                .hh-journey-grid { grid-template-columns: repeat(2, 1fr); }
                .hh-field-journey { grid-template-columns: repeat(3, 1fr); }
              }
              @media (max-width: 768px) {
                .hh-hero { min-height: auto !important; padding-bottom: 5rem !important; }
                .hh-hero-bg { position: absolute !important; inset: 0 !important; width: 100% !important; height: 100% !important; }
                .hh-hero-bg img { width: 100% !important; height: 100% !important; object-fit: cover !important; object-position: center top !important; }
                .hh-hero-overlay { background: linear-gradient(135deg, rgba(15, 23, 20, 0.88) 0%, rgba(27, 67, 50, 0.78) 55%, rgba(15, 23, 20, 0.45) 100%) !important; }
                .hh-hero-content { padding: 6.75rem 1.25rem 2.5rem !important; text-align: center !important; }
                .hh-hero-eyebrow { font-size: 0.85rem !important; font-weight: 700 !important; color: #74C69D !important; letter-spacing: 0.05em !important; background: rgba(116, 198, 157, 0.15) !important; padding: 6px 16px !important; border-radius: 99px !important; display: inline-flex !important; margin-bottom: 1.25rem !important; }
                .hh-hero-title { font-size: clamp(1.6rem, 5.5vw, 2.2rem) !important; font-weight: 800 !important; color: #ffffff !important; line-height: 1.25 !important; margin-bottom: 1.25rem !important; letter-spacing: -0.02em !important; max-width: 800px !important; margin-left: auto !important; margin-right: auto !important; }
                .hh-hero-sub { font-size: 0.95rem !important; color: rgba(255,255,255,0.88) !important; line-height: 1.7 !important; max-width: 800px !important; margin: 0 auto 2.5rem !important; }
                .hh-hero-gradient-bottom { display: block !important; }
                .hh-metrics-grid { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 0.85rem !important; margin: 1.5rem 0 2.5rem 0 !important; }
                .hh-metric-card { padding: 1.15rem 0.85rem !important; text-align: center !important; border-radius: 14px !important; box-sizing: border-box !important; }
                .hh-metric-num { font-size: clamp(1.8rem, 6vw, 2.3rem) !important; margin-bottom: 0.4rem !important; }
                .hh-metric-label { font-size: 0.85rem !important; line-height: 1.35 !important; }
                .hh-snapshot { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 0.85rem !important; padding: 1.15rem 1rem !important; border-radius: 16px !important; }
                .hh-snapshot > div:nth-child(3),
                .hh-snapshot > div:nth-child(4) { grid-column: span 2 !important; }
                .hh-context-grid, .hh-challenge-cards, .hh-field-layers, .hh-img-grid-4, .hh-research-perspectives, .hh-matrix, .hh-shift-grid { grid-template-columns: 1fr !important; }
                .hh-field-journey { grid-template-columns: repeat(2, 1fr) !important; }
                .hh-journey-grid { grid-template-columns: 1fr !important; }
                .hh-synthesis-chain { grid-template-columns: 1fr !important; }
                .hh-approach-grid { grid-template-columns: 1fr !important; }
              }
            `}</style>

            {/* ===== HERO ===== */}
            <section className="hh-hero">
              <img
                src="/images/Healing Herbar Store.jpg"
                alt="Healing Herbar modern storefront"
                className="hh-hero-bg"
              />
              <div className="hh-hero-overlay" />
              <div className="hh-hero-content">
                <h1 className="hh-hero-title">讓青草茶走進日常｜品牌再定位研究</h1>
                <p className="hh-hero-sub">
                  透過質性研究打破「視覺老化」的初期迷思，協助 50
                  年傳統青草茶品牌將轉型焦點從單純的視覺翻新，重新定錨於「建立日常連結」的體驗策略。
                </p>

                {/* ===== PROJECT SNAPSHOT INTEGRATED IN HERO ===== */}
                <div className="hh-snapshot">
                  <div className="hh-snapshot-item">
                    <div className="hh-snapshot-label">專案角色</div>
                    <div className="hh-snapshot-value">主導研究員</div>
                  </div>
                  <div className="hh-snapshot-item">
                    <div className="hh-snapshot-label">專案時間</div>
                    <div className="hh-snapshot-value">2024.09–2024.11</div>
                  </div>
                  <div className="hh-snapshot-item">
                    <div className="hh-snapshot-label">研究範疇</div>
                    <div className="hh-snapshot-value-sm">
                      Brand Study · Customer Experience · UX Research
                    </div>
                  </div>
                  <div className="hh-snapshot-item">
                    <div className="hh-snapshot-label">團隊架構</div>
                    <div className="hh-snapshot-value-sm">4 人研究團隊（指導教授：唐玄輝教授）</div>
                  </div>
                </div>
              </div>
              <div className="hh-hero-gradient-bottom" />
            </section>

            {/* ===== MAIN CONTAINER (Unified 1000px Width) ===== */}
            <div className="hh-inner">
              {/* ===== CONTEXT ===== */}
              <div className="hh-section">
                <div className="hh-eyebrow">品牌背景</div>
                <h2 className="hh-section-title">
                  挑戰在於如何將頭一次的好奇心，轉化為持續的長期連結？
                </h2>
                <div className="hh-context-grid">
                  <div className="hh-context-card hh-tradition">
                    <h4>
                      <span>傳統底蘊</span>
                    </h4>
                    <ul>
                      <li>創立於 1972 年</li>
                      <li>位於龍山寺旁的萬華青草巷</li>
                      <li>深耕五十餘年的家族傳承</li>
                      <li>豐富的傳統漢方青草知識</li>
                    </ul>
                  </div>
                  <div className="hh-context-card hh-modern">
                    <h4>
                      <span>現代轉型</span>
                    </h4>
                    <ul>
                      <li>明亮現代化的門市空間</li>
                      <li>創新特調漢方飲品</li>
                      <li>日常茶包、漢方調酒、草本藥浴包</li>
                      <li>多元化產品與服務體驗</li>
                    </ul>
                  </div>
                </div>
                <div className="hh-statement-box">
                  <p className="hh-statement-text">
                    「品牌成功吸引了年輕客群前來嘗鮮一次，卻尚未建立讓他們再次造訪的明確理由。」
                  </p>
                </div>
              </div>
            </div>

            {/* ===== RESEARCH CHALLENGE (Dark Fullbleed) ===== */}
            <div className="hh-fullbleed-dark">
              <div className="hh-inner">
                <div className="hh-eyebrow-light">研究挑戰</div>
                <h2 className="hh-section-title-light">為什麼年輕消費者不常喝青草茶？</h2>
                <div className="hh-challenge-cards">
                  <div className="hh-challenge-card">
                    <h4>法規限制與市場變遷</h4>
                    <p>
                      隨著中西醫普及、廟宇藥籤文化退場，加上手搖飲市場快速蓬勃發展，青草茶作為日常保健與退火解熱的功能性需求逐漸被削弱。
                    </p>
                  </div>
                  <div className="hh-challenge-card">
                    <h4>溝通與定位屏障</h4>
                    <p>
                      法規限制使得品牌無法直接強調青草茶的療效與保健功能。同時，年輕人在選擇飲品時，更受風味、社交場景與便利性驅動，而非單純考量健康。
                    </p>
                  </div>
                </div>
                <div className="hh-hmw-box">
                  <p className="hh-hmw-title">
                    品牌需要為年輕世代找到一個重新接觸、嘗試並持續回購青草茶的全新理由。
                  </p>
                </div>
              </div>
            </div>

            {/* ===== RESEARCH APPROACH ===== */}
            <div className="hh-inner">
              <div className="hh-section" style={{ marginTop: '5rem' }}>
                <div className="hh-eyebrow">研究方法</div>
                <h2 className="hh-section-title">將模糊的品牌議題，轉化為具體的質性研究架構</h2>
                <div className="hh-approach-grid">
                  <div className="hh-approach-card">
                    <div className="hh-approach-num">01</div>
                    <div className="hh-approach-title">界定核心問題</div>
                    <div className="hh-approach-desc">
                      將抽象的品牌挑戰轉化為可研究、具結構化的問題架構。
                    </div>
                  </div>
                  <div className="hh-approach-card">
                    <div className="hh-approach-num">02</div>
                    <div className="hh-approach-title">田野調查與訪談</div>
                    <div className="hh-approach-desc">
                      交叉分析品牌方、潛在目標客群與老主顧的三方視角。
                    </div>
                  </div>
                  <div className="hh-approach-card">
                    <div className="hh-approach-num">03</div>
                    <div className="hh-approach-title">收網行為模式</div>
                    <div className="hh-approach-desc">
                      歸納行為集群並繪製 2×2 人物誌矩陣以區隔目標客群。
                    </div>
                  </div>
                  <div className="hh-approach-card">
                    <div className="hh-approach-num">04</div>
                    <div className="hh-approach-title">策略轉化落地</div>
                    <div className="hh-approach-desc">
                      對齊顧客體驗旅程圖，提煉可落地執行的品牌策略機會點。
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== FIELD IMMERSION (Warm Fullbleed) ===== */}
            <div className="hh-fullbleed-warm">
              <div className="hh-inner">
                <div className="hh-section">
                  <div className="hh-eyebrow">場域走察</div>
                  <h2 className="hh-section-title">走過青草巷到門市：顧客體驗的全程走訪</h2>
                  <p className="hh-body">
                    我們多次前往萬華青草巷進行實地走訪與觀察，記錄實體環境、顧客行為及服務互動細節。研究發現，現有的體驗雖具文化特色，但整體旅程仍較為碎片化。
                  </p>

                  {/* Field Journey Steps */}
                  <div className="hh-field-journey">
                    <div className="hh-field-step">
                      <div className="hh-field-step-num">Step 1</div>
                      <div className="hh-field-step-label">抵達</div>
                      <div className="hh-field-step-desc">穿梭傳統狹窄巷弄</div>
                    </div>
                    <div className="hh-field-step">
                      <div className="hh-field-step-num">Step 2</div>
                      <div className="hh-field-step-label">進店</div>
                      <div className="hh-field-step-desc">感受現代門市第一印象</div>
                    </div>
                    <div className="hh-field-step">
                      <div className="hh-field-step-num">Step 3</div>
                      <div className="hh-field-step-label">探索</div>
                      <div className="hh-field-step-desc">瀏覽門市與產品陳列</div>
                    </div>
                    <div className="hh-field-step">
                      <div className="hh-field-step-num">Step 4</div>
                      <div className="hh-field-step-label">互動</div>
                      <div className="hh-field-step-desc">與店員溝通諮詢</div>
                    </div>
                    <div className="hh-field-step">
                      <div className="hh-field-step-num">Step 5</div>
                      <div className="hh-field-step-label">購買</div>
                      <div className="hh-field-step-desc">結帳與包裝拿取</div>
                    </div>
                    <div className="hh-field-step">
                      <div className="hh-field-step-num">Step 6</div>
                      <div className="hh-field-step-label">售後</div>
                      <div className="hh-field-step-desc">產品使用與再次造訪意願</div>
                    </div>
                  </div>

                  <h3
                    style={{
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      color: '#111827',
                      margin: '2.5rem 0 1.25rem',
                    }}
                  >
                    觀察的四個層面
                  </h3>
                  <div className="hh-field-layers">
                    <div className="hh-field-layer">
                      <h5>空間實體環境</h5>
                      <ul>
                        <li>巷弄位置較深，減少了自然人流的能見度</li>
                        <li>明亮的現代裝潢與周邊傳統街景形成強烈對比</li>
                        <li>店內內用座席有限，限制了駐足停留時間</li>
                      </ul>
                    </div>
                    <div className="hh-field-layer">
                      <h5>產品體驗細節</h5>
                      <ul>
                        <li>產品說明偏向傳統草本術語，初學者不易理解</li>
                        <li>缺少針對新手設計的低門檻入門產品</li>
                        <li>包裝視覺較少傳達日常飲用的具體場景</li>
                      </ul>
                    </div>
                    <div className="hh-field-layer">
                      <h5>服務互動流程</h5>
                      <ul>
                        <li>店員知識專業，但溝通多屬於被動回答</li>
                        <li>缺乏引導式的試飲與風味體驗環節</li>
                        <li>結帳與加入會員流程缺乏數位體驗點</li>
                      </ul>
                    </div>
                    <div className="hh-field-layer">
                      <h5>品牌溝通傳達</h5>
                      <ul>
                        <li>品牌歷史與創立故事在門市中較不易被主動感知</li>
                        <li>門市空間缺少社群媒體導流或數位互動視覺</li>
                        <li>缺乏離店後的顧客互動與會員忠誠培育機制</li>
                      </ul>
                    </div>
                  </div>

                  <div className="hh-img-grid-4">
                    <img src="/images/Herbal visit1.jpg" alt="Field visit photo 1" />
                    <img src="/images/Herbal visit2.jpg" alt="Field visit photo 2" />
                    <img src="/images/Herbal visit3.jpg" alt="Field visit photo 3" />
                    <img src="/images/Herbal visit4.jpg" alt="Field visit photo 4" />
                  </div>
                </div>
              </div>
            </div>

            {/* ===== RESEARCH DESIGN: THREE PERSPECTIVES ===== */}
            <div className="hh-inner">
              <div className="hh-section" style={{ marginTop: '5rem' }}>
                <div className="hh-eyebrow">研究設計</div>
                <h2 className="hh-section-title">透過三方視角，解構品牌與消費者的認知落差</h2>
                <p className="hh-body">
                  我們針對三大關鍵利害關係人群體進行半結構化深度訪談，交叉驗證品牌定位並找出體驗摩擦點。
                </p>
                <div className="hh-research-perspectives">
                  <div className="hh-perspective">
                    <div className="hh-persp-header">
                      <span className="hh-persp-badge">Group 01</span>
                    </div>
                    <h5>品牌經營團隊</h5>
                    <p>深入了解商業目標、品牌願景與經營團隊感知到的挑戰。</p>
                    <ul>
                      <li>品牌起源與文化傳承</li>
                      <li>對目標客群的既有假設</li>
                      <li>現有的行銷與推廣方式</li>
                      <li>未來的轉型與發展願景</li>
                    </ul>
                  </div>
                  <div className="hh-perspective">
                    <div className="hh-persp-header">
                      <span className="hh-persp-badge">Group 02</span>
                    </div>
                    <h5>年輕潛在客群</h5>
                    <p>探討年輕世代對青草茶的刻板印象、購買障礙與誘發動機。</p>
                    <ul>
                      <li>對品牌的認知與印象</li>
                      <li>青草茶與保健飲品的使用習慣</li>
                      <li>阻礙購買與誘發嘗試的關鍵因素</li>
                      <li>對理想飲用體驗的期待</li>
                    </ul>
                  </div>
                  <div className="hh-perspective">
                    <div className="hh-persp-header">
                      <span className="hh-persp-badge">Group 03</span>
                    </div>
                    <h5>熟客與老主顧</h5>
                    <p>了解老主顧持續回購的核心原因與情感連結。</p>
                    <ul>
                      <li>持續造訪與購買的動機</li>
                      <li>產品使用方式與生活場景</li>
                      <li>既有體驗的痛點與改善建議</li>
                      <li>對品牌的忠誠度與情感深度</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== PERSONA MATRIX (Warm Fullbleed) ===== */}
            <div className="hh-fullbleed-warm">
              <div className="hh-inner">
                <div className="hh-section">
                  <div className="hh-eyebrow">目標客群</div>
                  <h2 className="hh-section-title">交錯兩大面向，歸納出四大行為分眾</h2>
                  <p className="hh-body">
                    透過「健康意識高低」與「生活社交導向（個人導向 vs
                    社交導向）」兩大維度交叉分析，歸納出四大目標消費者集群。
                  </p>
                  <div className="hh-matrix-wrap">
                    <div className="hh-matrix">
                      <div className="hh-matrix-cell hh-core">
                        <div>
                          <div className="hh-matrix-core-badge">核心客群 🎯</div>
                          <div className="hh-matrix-name">健康生活享受者</div>
                          <div className="hh-matrix-desc">
                            高健康意識、偏好社交生活。喜愛與朋友分享健康生活體驗，重視產品視覺美感與門市氛圍。
                          </div>
                        </div>
                        <div className="hh-matrix-tags">
                          <span className="hh-matrix-tag">積極生活</span>
                          <span className="hh-matrix-tag">社群分享</span>
                          <span className="hh-matrix-tag">重視體驗</span>
                        </div>
                      </div>

                      <div className="hh-matrix-cell hh-core">
                        <div>
                          <div className="hh-matrix-core-badge">核心客群 🎯</div>
                          <div className="hh-matrix-name">精準保健實踐者</div>
                          <div className="hh-matrix-desc">
                            高健康意識、偏向個人導向。具備明確的健康訴求，主動尋求功能性產品與清晰的成分功效資訊。
                          </div>
                        </div>
                        <div className="hh-matrix-tags">
                          <span className="hh-matrix-tag">目標導向</span>
                          <span className="hh-matrix-tag">重視保健</span>
                          <span className="hh-matrix-tag">主動探求資訊</span>
                        </div>
                      </div>

                      <div className="hh-matrix-cell hh-secondary">
                        <div>
                          <div className="hh-matrix-name hh-muted">隨興流行探索者</div>
                          <div className="hh-matrix-desc hh-muted">
                            健康意識較低、偏好社交生活。容易受流行趨勢與朋友推薦影響做出即興購買決策。
                          </div>
                        </div>
                        <div className="hh-matrix-tags">
                          <span className="hh-matrix-tag hh-muted">趨勢驅動</span>
                          <span className="hh-matrix-tag hh-muted">隨興嘗試</span>
                        </div>
                      </div>

                      <div className="hh-matrix-cell hh-secondary">
                        <div>
                          <div className="hh-matrix-name hh-muted">實用需求解決者</div>
                          <div className="hh-matrix-desc hh-muted">
                            健康意識較低、偏向個人導向。僅在身體出現特定需求（如火氣大、熬夜）時才考慮購買。
                          </div>
                        </div>
                        <div className="hh-matrix-tags">
                          <span className="hh-matrix-tag hh-muted">特定需求觸發</span>
                          <span className="hh-matrix-tag hh-muted">價格敏感</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== THE TURNING POINT (Dark Fullbleed) ===== */}
            <div className="hh-fullbleed-dark">
              <div className="hh-inner">
                <div className="hh-eyebrow-light">關鍵轉折點</div>
                <h2 className="hh-section-title-light">從視覺形象翻新，轉向日常生活的相關性</h2>
                <p className="hh-body-light">
                  初始假設認為品牌需要全面更新視覺識別（VI），但深入研究揭示了更關鍵的本質問題。
                </p>
                <div className="hh-shift-grid">
                  <div className="hh-shift-card before">
                    <span className="hh-shift-badge">初始假設</span>
                    <div className="hh-shift-title">「我們需要更年輕的品牌形象」</div>
                    <div className="hh-shift-text">
                      經營團隊認為核心問題在於視覺包裝：標誌、包裝與門市裝潢不夠符合年輕世代的審美。
                    </div>
                  </div>
                  <div className="hh-shift-card finding">
                    <span className="hh-shift-badge">關鍵研究發現</span>
                    <div className="hh-shift-title">問題出在「生活相關性」，而非「視覺美感」</div>
                    <div className="hh-shift-text">
                      年輕消費者其實相當欣賞門市的現代質感，真正的門檻是不知道「為什麼要在日常生活中選擇青草茶」。
                    </div>
                  </div>
                  <div className="hh-shift-card shift">
                    <span className="hh-shift-badge">策略思考轉型</span>
                    <div className="hh-shift-title">融入日常情境</div>
                    <div className="hh-shift-text">
                      策略焦點從「如何看起來更年輕？」轉變為「如何讓青草茶成為年輕人日常生活中自然的一環？」
                    </div>
                  </div>
                </div>

                <div className="hh-hmw-box">
                  <p className="hh-hmw-title">
                    「我們該如何讓青草茶不再被視為苦澀的傳統藥飲，而是成為年輕世代日常生活中自然、理想的健康生活方式選擇？」
                  </p>
                </div>
              </div>
            </div>

            {/* ===== THREE STRATEGIC INSIGHTS ===== */}
            <div className="hh-inner">
              <div className="hh-section" style={{ marginTop: '5rem' }}>
                <div className="hh-eyebrow">策略洞察</div>
                <h2 className="hh-section-title">重塑品牌策略的三大關鍵洞察</h2>

                {/* Insight 01 */}
                <div className="hh-insight-card">
                  <span className="hh-insight-tag">洞察 01 · 社交橋樑</span>
                  <h3 className="hh-insight-title">將青草茶打造為共享的體驗媒介</h3>
                  <div className="hh-insight-content">
                    <p className="hh-insight-p">
                      年輕消費者很少單獨購買青草茶，但非常願意在社交場景下（如朋友推薦或一同探索門市）嘗試。社交元素能顯著降低嘗試門檻，將陌生的飲品轉化為共同探索的樂趣。
                    </p>
                    <div className="hh-insight-impl-box">
                      <div className="hh-insight-impl-label">策略落地方向</div>
                      <p className="hh-insight-impl-text">
                        專為社交探索設計：打造適合打卡分享的視覺焦點、適合同樂的體驗形式，以及能誘發自然口碑傳播的互動體驗。
                      </p>
                    </div>
                  </div>
                </div>

                {/* Insight 02 */}
                <div className="hh-insight-card">
                  <span className="hh-insight-tag">洞察 02 · 觸及便利性</span>
                  <h3 className="hh-insight-title">便利性優先於健康意圖</h3>
                  <div className="hh-insight-content">
                    <p className="hh-insight-p">
                      即便是重視健康的年輕人，在日常選擇飲品時，「便利性」依然優先於「健康訴求」。僅靠單一巷弄門市會形成接觸障礙，難以培養日常飲用習慣。
                    </p>
                    <div className="hh-insight-impl-box">
                      <div className="hh-insight-impl-label">策略落地方向</div>
                      <p className="hh-insight-impl-text">
                        降低取得門檻與摩擦：探索快閃店、線上訂購、訂閱制與合作通路，讓產品更容易被購買。
                      </p>
                    </div>
                  </div>
                </div>

                {/* Insight 03 */}
                <div className="hh-insight-card">
                  <span className="hh-insight-tag">洞察 03 · 入門體驗</span>
                  <h3 className="hh-insight-title">創造令人難忘的首次體驗</h3>
                  <div className="hh-insight-content">
                    <p className="hh-insight-p">
                      決定消費者是否回購的關鍵，在於「感官記憶」（風味、香氣、空間氛圍），而非抽象的健康宣傳。精心引導的第一次體驗能建立深刻的情感錨點。
                    </p>
                    <div className="hh-insight-impl-box">
                      <div className="hh-insight-impl-label">策略落地方向</div>
                      <p className="hh-insight-impl-text">
                        設計引人入勝的入門體驗：例如品飲組合體驗（Tasting
                        Flight）、趣味草本科普互動與質感帶走禮。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== STRATEGY ENGINE — Unified Framework → Execution → Opportunity Flow ===== */}
            <div className="hh-fullbleed-dark">
              <div className="hh-inner hh-engine-inner">
                <div className="hh-engine">
                  <div className="hh-engine-intro">
                    <div className="hh-eyebrow">策略框架</div>
                    <h2 className="hh-section-title-light">將洞察收網為連貫的品牌策略框架</h2>
                    <p className="hh-engine-lead">
                      三大洞察交織出一條清晰的策略敘事——從「小眾功能性門市」邁向「融入日常的社交生活方式」。策略框架先定義品牌顧客的關鍵階段，接著展開三大落地執行方向，最後對齊到顧客旅程中的具體機會點。
                    </p>
                  </div>

                  {/* Layer 1: Framework — 4-stage journey */}
                  <div className="hh-engine-layer">
                    <div className="hh-engine-layer-label">策略框架 · 顧客旅程四階段</div>
                    <div className="hh-engine-stages">
                      {[
                        { step: '階段 1', title: '初次相遇', desc: '透過社交探索降低嘗試門檻' },
                        {
                          step: '階段 2',
                          title: '深度嘗試',
                          desc: '難忘的首次體驗建立感官與情感錨點',
                        },
                        {
                          step: '階段 3',
                          title: '社群分享',
                          desc: '具分享價值的體驗擴大自然口碑傳播',
                        },
                        {
                          step: '階段 4',
                          title: '持續回購',
                          desc: '多元便利的管道培養日常飲用習慣',
                        },
                      ].map((s, i) => (
                        <Fragment key={i}>
                          {i > 0 && <div className="hh-engine-flow-arrow">→</div>}
                          <div className="hh-engine-stage">
                            <div className="hh-engine-stage-step">{s.step}</div>
                            <div className="hh-engine-stage-title">{s.title}</div>
                            <div className="hh-engine-stage-desc">{s.desc}</div>
                          </div>
                        </Fragment>
                      ))}
                    </div>
                  </div>

                  {/* Layer 2: Execution — 3 strategic directions */}
                  <div className="hh-engine-layer">
                    <div className="hh-engine-layer-label">落地執行 · 三大策略方向</div>
                    <div className="hh-engine-strategies">
                      <div className="hh-engine-strategy">
                        <div className="hh-engine-strategy-num">策略 01</div>
                        <div className="hh-engine-strategy-title">社交體驗設計</div>
                        <p className="hh-engine-strategy-desc">
                          將門市打造為社交探索目的地，提供適合多人共享的品飲形式、體驗活動與拍照打卡場景。
                        </p>
                        <div className="hh-engine-strategy-tag">對齊洞察 01 · 社交橋樑</div>
                      </div>
                      <div className="hh-engine-strategy">
                        <div className="hh-engine-strategy-num">策略 02</div>
                        <div className="hh-engine-strategy-title">多元便捷通路</div>
                        <p className="hh-engine-strategy-desc">
                          突破單一門市限制，透過跨界快閃、線上商城、訂閱方案與獨立咖啡館合作擴大觸及。
                        </p>
                        <div className="hh-engine-strategy-tag">對齊洞察 02 · 觸及便利性</div>
                      </div>
                      <div className="hh-engine-strategy">
                        <div className="hh-engine-strategy-num">策略 03</div>
                        <div className="hh-engine-strategy-title">新手引導體驗</div>
                        <p className="hh-engine-strategy-desc">
                          設計針對首訪顧客的體驗旅程，結合風味導覽、草本知識互動與帶走紀念禮。
                        </p>
                        <div className="hh-engine-strategy-tag">對齊洞察 03 · 入門體驗</div>
                      </div>
                    </div>
                  </div>

                  {/* Layer 3: Opportunity map — aligned to journey touchpoints */}
                  <div className="hh-engine-layer">
                    <div className="hh-engine-layer-label">機會點 · 對齊旅程五大接觸點</div>
                    <div className="hh-engine-opps">
                      {[
                        {
                          stage: '1 品牌認知',
                          desc: '社群媒體擴散、網紅跨界合作、口碑傳播',
                          tag: '社交體驗',
                        },
                        {
                          stage: '2 評估考慮',
                          desc: '清晰的飲品分類、新手友善的入門指引',
                          tag: '新手引導',
                        },
                        {
                          stage: '3 門市造訪',
                          desc: '專業品飲導覽、沉浸式空間、視覺打卡點',
                          tag: '社交體驗',
                        },
                        {
                          stage: '4 產品購買',
                          desc: '簡化決策流程、組合體驗包、定期訂閱',
                          tag: '多元通路',
                        },
                        {
                          stage: '5 售後互動',
                          desc: '數位管道追蹤、回購提醒、會員忠誠計畫',
                          tag: '多元通路',
                        },
                      ].map((o, i) => (
                        <Fragment key={i}>
                          {i > 0 && <div className="hh-engine-opp-arrow">→</div>}
                          <div className="hh-engine-opp">
                            <div className="hh-engine-opp-stage">{o.stage}</div>
                            <p className="hh-engine-opp-desc">{o.desc}</p>
                            <div className="hh-engine-opp-tag">{o.tag}</div>
                          </div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== RESEARCH CONTRIBUTION ===== */}
            <div className="hh-inner">
              <div className="hh-section" style={{ marginTop: '5rem' }}>
                <h2 className="hh-section-title">本研究為品牌產出的核心價值</h2>
                <div className="hh-contrib-grid">
                  <div className="hh-contrib-card">
                    <div className="hh-contrib-num">01</div>
                    <div className="hh-contrib-title">重塑核心問題界定</div>
                    <div className="hh-contrib-desc">
                      引導品牌經營團隊將資源從純視覺翻新，轉向提升日常生活相關性，避免不必要的重新設計成本。
                    </div>
                  </div>
                  <div className="hh-contrib-card">
                    <div className="hh-contrib-num">02</div>
                    <div className="hh-contrib-title">可落地的人物誌矩陣</div>
                    <div className="hh-contrib-desc">
                      提供具操作性的 2×2 消費者矩陣，協助團隊明確鎖定核心客群並精準溝通。
                    </div>
                  </div>
                  <div className="hh-contrib-card">
                    <div className="hh-contrib-num">03</div>
                    <div className="hh-contrib-title">具體的策略發展藍圖</div>
                    <div className="hh-contrib-desc">
                      產出三大相互扣合的策略方向，並對齊顧客體驗旅程各個關鍵接觸點。
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== REFLECTION (Warm Fullbleed) ===== */}
            <div className="hh-fullbleed-warm">
              <div className="hh-inner">
                <div className="hh-section">
                  <h2 className="hh-section-title">心得與啟示</h2>

                  <div className="hh-reflection-card">
                    <span className="hh-reflection-num">反思 01</span>
                    <div className="hh-reflection-title">最有價值的洞察，往往顛覆最初的假設</div>
                    <div className="hh-reflection-text">
                      客戶最初期待的是視覺識別更新，但研究證明核心瓶頸在於缺乏日常生活的相關性。這再次印證策略必須由實證數據驅動，而非憑空假設。
                    </div>
                  </div>

                  <div className="hh-reflection-card">
                    <span className="hh-reflection-num">反思 02</span>
                    <div className="hh-reflection-title">多元三角驗證，大幅提升研究發現的信度</div>
                    <div className="hh-reflection-text">
                      交叉訪談品牌團隊、潛在客群與熟客三大群體，能客觀檢視內部假設與真實顧客反饋之間的落差，讓策略結論更加堅實。
                    </div>
                  </div>

                  <div className="hh-reflection-card">
                    <span className="hh-reflection-num">反思 03</span>
                    <div className="hh-reflection-title">
                      能直接指引行動的研究，才具備真正影響力
                    </div>
                    <div className="hh-reflection-text">
                      將洞察對齊顧客體驗旅程中的每一個接觸點，讓研究成果能夠迅速轉化為客戶團隊可立即執行的落地行動方案。
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '2.5rem' }}>
                    <button
                      onClick={() => router.push('/#work')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        backgroundColor: '#ffffff',
                        color: '#0f172a',
                        border: 'none',
                        padding: '0.9rem 2.25rem',
                        borderRadius: '9999px',
                        fontSize: '1rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 10px 25px rgba(255, 255, 255, 0.15)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <ArrowLeft size={18} />
                      返回作品列表
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      } else if (isApex) {
        return (
          /* ====== Bookstore Usability Study Case Study Detail View ====== */
          <div className="case-study-container">
            <SharedProjectHero
              title="Usability Study of Taiwan's Three Major Online Bookstore Platforms"
              description="Comparing the usability of Eslite Online, Books.com.tw, and Kingstone through task testing, Think Aloud, SUS scale, and one-way ANOVA"
              background="url(https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?q=80&w=1600&auto=format&fit=crop)"
            />

            {/* Project Meta */}
            <div className="case-study-content-grid">
              <TableOfContents />
              <div className="case-study-main-content">
                <SharedProjectMetaBar
                  items={[
                    { label: 'Role', value: 'UX Researcher' },
                    { label: 'Date', value: '2025' },
                    { label: 'Method', value: 'Task Testing · Think Aloud · SUS · ANOVA' },
                    {
                      label: 'Participants',
                      value:
                        '30 participants · Ages 20–59 · With online book purchasing experience',
                    },
                  ]}
                />

                <div className="case-study-body">
                  {/* Why This Research */}
                  <div className="case-study-section">
                    <h3 className="case-study-section-title">Why This Research</h3>
                    <p>
                      Taiwan's online book sales surpassed 50% of the total book market in 2023, yet
                      the user experience of the three major platforms had never been systematically
                      compared. The question this research aimed to answer was very specific: when
                      performing the same tasks, how much impact do the design differences across
                      the three platforms actually have on users?
                    </p>
                  </div>

                  {/* Research Design */}
                  <div className="case-study-section">
                    <h3 className="case-study-section-title">Research Design</h3>
                    <div className="case-study-quote-box">
                      <span className="case-study-quote-label">Methodology</span>
                      <p>
                        Within-subjects design × Task Testing × Think Aloud × SUS Scale × One-way
                        ANOVA
                      </p>
                    </div>

                    <p>
                      Using a within-subjects design, each participant completed five core tasks
                      across the three websites in random order, covering new book browsing, book
                      search, bookmark/favorites, cart operations, and account management. Tasks
                      simulated real purchasing scenarios, with completion time as the primary
                      efficiency metric. After tasks, participants filled out the SUS (System
                      Usability Scale) questionnaire, supplemented by in-depth interviews for
                      qualitative feedback.
                    </p>
                    <p>
                      Data analysis used one-way ANOVA to test whether differences among the three
                      platforms were statistically significant. For significant results, LSD
                      post-hoc tests were conducted to identify the sources of differences.
                    </p>
                  </div>

                  {/* Key Findings */}
                  <div className="case-study-section">
                    <h3 className="case-study-section-title">Key Findings</h3>

                    <p>
                      Among the five tasks, three showed statistically significant differences
                      across the three platforms (p {'<'} .05) — meaning the design differences in
                      these features were numerically verifiable.
                    </p>

                    <div className="case-study-sub-card" style={{ margin: '0 0 1rem' }}>
                      <span className="case-study-sub-card-title">
                        📌 Bookmark Feature: The fastest was 2.9x faster than the slowest for the
                        same task
                      </span>
                      <p
                        style={{
                          fontSize: '1rem',
                          color: '#1f2937',
                          marginTop: '0.35rem',
                        }}
                      >
                        The bookmark task results were the most striking. Eslite Online averaged
                        30.8 seconds, Books.com.tw 47.5 seconds, and Kingstone took 89.9 seconds —
                        2.9 times longer than Eslite Online (p = .001).
                      </p>
                      <p
                        style={{
                          fontSize: '1rem',
                          color: '#1f2937',
                          marginTop: '0.35rem',
                        }}
                      >
                        Even more notable was the dispersion. Kingstone's standard deviation was
                        45.8 seconds, more than double Eslite Online's (SD = 19.1 seconds). The
                        fastest participant completed it in 44 seconds, while the slowest took 160
                        seconds. This indicates not just "slowness," but that design inconsistency
                        caused severe divergence in experience — familiar users could find it,
                        unfamiliar users almost couldn't.
                      </p>
                      <p
                        style={{
                          fontSize: '1rem',
                          color: '#1f2937',
                          marginTop: '0.35rem',
                        }}
                      >
                        Interviews confirmed this finding: Kingstone's bookmark entry was hidden in
                        an inconspicuous position on the book detail page, lacking clear text
                        labels, forcing most participants to trial-and-error to locate it. Eslite
                        Online, on the other hand, presented a heart icon paired with "Bookmark"
                        (bookmark) text side by side, with a clear path and intuitive response.
                      </p>
                    </div>

                    <div className="case-study-sub-card" style={{ margin: '0 0 1rem' }}>
                      <span className="case-study-sub-card-title">
                        🛒 Cart Function: Response speed is the core of the experience
                      </span>
                      <p
                        style={{
                          fontSize: '1rem',
                          color: '#1f2937',
                          marginTop: '0.35rem',
                        }}
                      >
                        The cart task also showed significant differences (p = .001). Kingstone
                        (18.4 sec) and Books.com.tw (23.1 sec) performed similarly, while Eslite
                        Online (39.2 sec) lagged significantly behind.
                      </p>
                      <p
                        style={{
                          fontSize: '1rem',
                          color: '#1f2937',
                          marginTop: '0.35rem',
                        }}
                      >
                        Eslite Online's slowness in the cart task came not from interface
                        complexity, but from page loading speed. Participants commonly reported that
                        "waiting for the page to respond" took up most of the time. This put Eslite
                        Online in a paradox: its interface design was well-received, but technical
                        latency dragged down the operational experience.
                      </p>
                    </div>

                    <div className="case-study-sub-card" style={{ margin: '0 0 1rem' }}>
                      <span className="case-study-sub-card-title">
                        📖 Book Detail Page: Kingstone's advantage in information presentation
                      </span>
                      <p
                        style={{
                          fontSize: '1rem',
                          color: '#1f2937',
                          marginTop: '0.35rem',
                        }}
                      >
                        In the task of searching and confirming book information (author, price,
                        discounts), Kingstone (21.4 sec) and Books.com.tw (22.6 sec) performed
                        similarly, while Eslite Online (33.5 sec) was slower (p = .040). Interviews
                        indicated that Kingstone and Books.com.tw had clearly layered information on
                        their book detail pages, with key fields (price, discount, author) having
                        clear visual hierarchy; Eslite Online's same page had higher information
                        density, and loading time affected smoothness.
                      </p>
                    </div>
                  </div>

                  {/* SUS Scale */}
                  <div className="case-study-section">
                    <h3 className="case-study-section-title">
                      SUS Scale: The Story Beyond Average Scores
                    </h3>

                    <p>
                      The three platforms' average SUS scores were Books.com.tw 66.25, Eslite Online
                      65.90, and Kingstone 59.50, with no statistically significant difference (p =
                      .676) — from the averages, the gap was not large.
                    </p>

                    <p>
                      But looking at standard deviation, another noteworthy phenomenon emerged.
                      Eslite Online's SD was 25.7, with individual scores ranging from 16.5 to 100 —
                      a span of 83.5 points. Books.com.tw's SD was only 12.5, with a span of 37.5.
                      This means Eslite Online's average was nearly identical to Books.com.tw's, but
                      the consistency of evaluations was far lower — some found it the most usable
                      platform, while others found it nearly unusable.
                    </p>

                    <p>
                      This finding echoed the qualitative interview feedback: Eslite Online's
                      feature layout made sense to familiar users, but provided almost no guidance
                      for first-time users, resulting in high learning costs and consequently
                      greater retention risk.
                    </p>
                  </div>

                  {/* Design Insights */}
                  <div className="case-study-section">
                    <h3 className="case-study-section-title">Design Insights</h3>

                    <p>
                      This research showed me that the three platforms didn't differ much in terms
                      of "whether basic features exist." What truly created experience gaps were
                      more subtle factors: feature visibility, immediacy of operational feedback,
                      and page response speed.
                    </p>

                    <p>
                      Eslite Online received the most praise for visual design, but loading speed
                      issues offset these positive impressions. Kingstone's interface was
                      information-rich, but the naming and placement of feature entries imposed high
                      learning costs on first-time users. Books.com.tw was the most balanced
                      overall, but some operations in the shopping flow (such as the juxtaposition
                      of "Buy Later" and "Add to Cart") were prone to accidental clicks.
                    </p>

                    <p>
                      From the research data, we derived five specific design recommendations:
                      simplify interface hierarchy to reduce cognitive load, optimize icons and text
                      labels for key functions, improve page loading performance, enhance visibility
                      of recommended content, and increase the immediacy of operational feedback.
                    </p>
                  </div>

                  {/* Research Reflection */}
                  <div className="case-study-section">
                    <h3 className="case-study-section-title">Research Reflection</h3>
                    <p>
                      This research gave me a clearer understanding of "the granularity of usability
                      testing." ANOVA told me where differences existed, but not why — the real
                      explanations came from participants' verbal expressions during tasks and their
                      subjective descriptions in post-test interviews. Quantitative data established
                      the existence of problems; qualitative data provided the design direction.
                    </p>
                    <p>
                      If I were to do it again, I would add error count tracking to the task design,
                      because "how long it took" and "how many wrong steps were taken" are two
                      different dimensions of the problem — some participants took a long time but
                      followed the correct path, while others completed quickly but took detours
                      along the way. These two scenarios have different implications for design
                      improvement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
        // [COMMENTED OUT - Post Office case study now links to Medium article]
        // } else if (isScooter) {
        //   return (
        //     <div className="post-case-root">
        // <style>{`
        // /* ===== Post Office UX Research Case Study ===== */
        // .post-section { margin-bottom: 5rem; }
        // .post-section:last-child { margin-bottom: 0; }
        // .post-section-label {
        // font-size: 0.75rem; font-weight: 700; color: rgba(0,0,0,0.3);
        // text-transform: uppercase; letter-spacing: 0.12em; margin-bottom: 0.75rem;
        // }
        // .post-fullbleed {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // padding: 5rem clamp(1.5rem, 5vw, 4rem);
        // position: relative; overflow: hidden;
        // }
        // .post-fullbleed-dark {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // background: #0f0f0f;
        // padding: 5rem clamp(1.5rem, 5vw, 4rem);
        // position: relative; overflow: hidden;
        // }
        // .post-fullbleed-dark .post-section-label { color: rgba(255,255,255,0.4); }
        // .post-fullbleed-dark h2 { color: #ffffff; }
        // .post-fullbleed-dark h3 { color: #e2e8f0; }
        // .post-fullbleed-dark h4 { color: #e2e8f0; }
        // .post-fullbleed-dark p { color: rgba(255,255,255,0.7); }
        // .post-fullbleed-dark .post-quote-text { color: rgba(255,255,255,0.9); }
        // .post-fullbleed-warm {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // background: #fafaf8;
        // padding: 5rem clamp(1.5rem, 5vw, 4rem);
        // position: relative; overflow: hidden;
        // }
        // .post-fullbleed-gray {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // background: #f2f2f2;
        // padding: 5rem clamp(1.5rem, 5vw, 4rem);
        // position: relative; overflow: hidden;
        // }
        // .post-fullbleed-green {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // background: #0a3d2e;
        // padding: 5rem clamp(1.5rem, 5vw, 4rem);
        // position: relative; overflow: hidden;
        // }
        // .post-fullbleed-green .post-section-label { color: rgba(255,255,255,0.4); }
        // .post-fullbleed-green h2 { color: #ffffff; }
        // .post-fullbleed-green h3 { color: #e2e8f0; }
        // .post-fullbleed-green p { color: rgba(255,255,255,0.8); }
        // .post-inner {
        // max-width: 1100px; margin: 0 auto;
        // }
        // .post-inner-narrow {
        // max-width: 800px; margin: 0 auto;
        // }
        // .post-inner p {
        // font-size: clamp(0.95rem, 1.1vw, 1.1rem);
        // line-height: 1.8; color: rgba(0,0,0,0.75); margin-bottom: 1.25rem;
        // }
        // .post-inner p:last-child { margin-bottom: 0; }
        // .post-inner-narrow p {
        // font-size: clamp(0.95rem, 1.1vw, 1.1rem);
        // line-height: 1.8; color: rgba(0,0,0,0.75); margin-bottom: 1.25rem;
        // }
        // .post-inner-narrow p:last-child { margin-bottom: 0; }

        // /* Hero - temple style with image as background */
        // .post-hero {
        // position: relative; width: 100vw;
        // margin-left: calc(-50vw + 50%);
        // overflow: hidden;
        // min-height: 70vh;
        // display: flex; align-items: center;
        // }
        // .post-hero-bg { position: absolute; inset: 0; z-index: 0; }
        // .post-hero-bg img { width: 100%; height: 100%; object-fit: cover; }
        // .post-hero-overlay {
        // position: absolute; inset: 0;
        // background: linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.3) 100%);
        // z-index: 1;
        // }
        // .post-hero-content {
        // position: relative; z-index: 2;
        // max-width: 1100px; margin: 0 auto;
        // padding: 6rem clamp(1.5rem, 5vw, 4rem);
        // }
        // .post-hero-label {
        // font-size: 0.8rem; font-weight: 700; color: #b8860b;
        // text-transform: uppercase; letter-spacing: 0.15em; margin-bottom: 1rem;
        // }
        // .post-hero-content h1 {
        // font-size: clamp(2rem, 4vw, 3.5rem);
        // font-weight: 700; color: #ffffff;
        // line-height: 1.2; margin: 0 0 1.25rem 0;
        // max-width: 900px;
        // }
        // .post-hero-desc {
        // font-size: clamp(1rem, 1.3vw, 1.2rem);
        // line-height: 1.7; color: rgba(255,255,255,0.7);
        // max-width: 700px; margin: 0;
        // }
        // .post-hero-gradient-bottom {
        // position: absolute; bottom: 0; left: 0; right: 0; height: 200px;
        // background: linear-gradient(to bottom, transparent, #ffffff);
        // z-index: 2; pointer-events: none;
        // }

        // /* Snapshot */
        // .post-snapshot {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // background: #fafaf8;
        // padding: 1.5rem clamp(1.5rem, 5vw, 4rem);
        // border-bottom: 1px solid rgba(0,0,0,0.06);
        // }
        // .post-snapshot-inner {
        // max-width: 1100px; margin: 0 auto;
        // display: flex; flex-wrap: wrap; gap: 2rem;
        // align-items: flex-start;
        // }
        // .post-snapshot-item {
        // display: flex; flex-direction: column; gap: 0.2rem;
        // }
        // .post-snapshot-label {
        // font-size: 0.7rem; font-weight: 700; color: rgba(0,0,0,0.35);
        // text-transform: uppercase; letter-spacing: 0.1em;
        // }
        // .post-snapshot-value {
        // font-size: 0.9rem; color: rgba(0,0,0,0.8); font-weight: 500;
        // }

        // /* Research Question */
        // .post-rq {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // background: #0a3d2e;
        // padding: 5rem clamp(1.5rem, 5vw, 4rem);
        // position: relative; overflow: hidden;
        // }
        // .post-rq-inner {
        // max-width: 900px; margin: 0 auto;
        // }
        // .post-rq-label {
        // font-size: 0.75rem; font-weight: 700; color: rgba(255,255,255,0.4);
        // text-transform: uppercase; letter-spacing: 0.12em; margin-bottom: 1.5rem;
        // }
        // .post-rq-text {
        // font-size: clamp(1.4rem, 2.5vw, 2rem);
        // font-weight: 600; color: #ffffff;
        // line-height: 1.4; margin-bottom: 1.5rem;
        // }
        // .post-rq-context {
        // font-size: clamp(0.9rem, 1.1vw, 1.05rem);
        // line-height: 1.8; color: rgba(255,255,255,0.7);
        // max-width: 700px; margin: 0;
        // }

        // /* Research Framing */
        // .post-framing-grid {
        // display: grid;
        // grid-template-columns: repeat(4, 1fr);
        // gap: 1.5rem; margin-top: 2rem;
        // }
        // .post-framing-step {
        // background: #ffffff; border-radius: 12px;
        // padding: 1.75rem;
        // box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        // }
        // .post-framing-step-num {
        // font-size: 0.7rem; font-weight: 700; color: #b8860b;
        // letter-spacing: 0.08em; margin-bottom: 0.5rem;
        // }
        // .post-framing-step h4 {
        // font-size: 1rem; font-weight: 600; margin: 0 0 0.5rem 0;
        // color: rgba(0,0,0,0.85);
        // }
        // .post-framing-step p {
        // font-size: 0.85rem; line-height: 1.6; color: rgba(0,0,0,0.6);
        // margin: 0;
        // }

        // /* Research Numbers */
        // .post-numbers {
        // display: flex; gap: 3rem; justify-content: center;
        // margin-top: 2rem;
        // }
        // .post-numbers-item {
        // text-align: center;
        // }
        // .post-numbers-num {
        // font-size: clamp(2.5rem, 4vw, 3.5rem);
        // font-weight: 700; color: #b8860b;
        // line-height: 1; margin-bottom: 0.35rem;
        // }
        // .post-numbers-label {
        // font-size: 0.9rem; color: rgba(0,0,0,0.6);
        // }

        // /* Task Journey */
        // .post-task-journey {
        // display: flex; gap: 1.5rem; margin-top: 2rem;
        // }
        // .post-task-card {
        // flex: 1; background: #ffffff; border-radius: 12px;
        // padding: 1.75rem;
        // box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        // }
        // .post-task-card-icon {
        // font-size: 1.5rem; margin-bottom: 0.75rem;
        // }
        // .post-task-card h4 {
        // font-size: 1rem; font-weight: 600; margin: 0 0 0.5rem 0;
        // color: rgba(0,0,0,0.85);
        // }
        // .post-task-card p {
        // font-size: 0.85rem; line-height: 1.6; color: rgba(0,0,0,0.6);
        // margin: 0;
        // }

        // /* Process Flow */
        // .post-process-flow {
        // display: grid;
        // grid-template-columns: repeat(3, 1fr);
        // gap: 1.5rem; margin-top: 2rem;
        // }
        // .post-process-step {
        // background: #ffffff; border-radius: 12px;
        // padding: 1.75rem; text-align: center;
        // box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        // }
        // .post-process-step-num {
        // font-size: 0.7rem; font-weight: 700; color: #b8860b;
        // letter-spacing: 0.08em; margin-bottom: 0.5rem;
        // }
        // .post-process-step h4 {
        // font-size: 1rem; font-weight: 600; margin: 0 0 0.5rem 0;
        // color: rgba(0,0,0,0.85);
        // }
        // .post-process-step p {
        // font-size: 0.85rem; line-height: 1.6; color: rgba(0,0,0,0.6);
        // margin: 0;
        // }
        // .post-process-time {
        // font-size: 0.75rem; font-weight: 600; color: #b8860b;
        // margin-top: 0.5rem;
        // }

        // /* Participants Table */
        // .post-participants {
        // width: 100%; border-collapse: collapse;
        // font-size: 0.9rem; margin-top: 1.5rem;
        // }
        // .post-participants th {
        // text-align: left; padding: 0.75rem 1rem;
        // font-size: 0.75rem; font-weight: 700; color: rgba(0,0,0,0.4);
        // text-transform: uppercase; letter-spacing: 0.08em;
        // border-bottom: 2px solid rgba(0,0,0,0.08);
        // }
        // .post-participants td {
        // padding: 0.75rem 1rem;
        // border-bottom: 1px solid rgba(0,0,0,0.06);
        // color: rgba(0,0,0,0.75);
        // }
        // .post-participants tr:last-child td { border-bottom: none; }

        // /* Think Aloud Evidence */
        // .post-ta-grid {
        // display: grid;
        // grid-template-columns: 1.2fr 1fr;
        // gap: 2rem; margin-top: 2rem;
        // align-items: start;
        // }
        // .post-ta-img-wrap {
        // border-radius: 8px; overflow: hidden;
        // box-shadow: 0 2px 12px rgba(0,0,0,0.08);
        // }
        // .post-ta-img-wrap img {
        // width: 100%; height: auto; display: block;
        // }
        // .post-ta-annotations {
        // display: flex; flex-direction: column; gap: 1rem;
        // }
        // .post-ta-item {
        // display: flex; align-items: flex-start; gap: 0.75rem;
        // padding: 1rem; background: #ffffff; border-radius: 8px;
        // box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        // }
        // .post-ta-dot {
        // width: 10px; height: 10px; border-radius: 50%;
        // background: #b8860b; flex-shrink: 0; margin-top: 0.4rem;
        // }
        // .post-ta-item span {
        // font-size: 0.85rem; line-height: 1.5; color: rgba(0,0,0,0.7);
        // }
        // .post-ta-item strong { color: rgba(0,0,0,0.9); }

        // /* Representative Case - User Path */
        // .post-user-path {
        // margin-top: 2rem;
        // }
        // .post-path-step {
        // display: flex; gap: 1.5rem;
        // padding: 1.25rem 0;
        // border-bottom: 1px solid rgba(255,255,255,0.08);
        // }
        // .post-path-step:last-child { border-bottom: none; }
        // .post-path-num {
        // font-size: 0.75rem; font-weight: 700; color: #b8860b;
        // flex-shrink: 0; width: 2rem; padding-top: 0.15rem;
        // }
        // .post-path-content { flex: 1; }
        // .post-path-location {
        // font-size: 0.8rem; font-weight: 700; color: #b8860b;
        // text-transform: uppercase; letter-spacing: 0.08em;
        // flex-shrink: 0; width: 8rem; padding-top: 0.15rem;
        // }
        // .post-path-desc {
        // font-size: 0.9rem; line-height: 1.6; color: rgba(255,255,255,0.75);
        // margin: 0 0 0.5rem 0;
        // }
        // .post-path-desc:last-child { margin-bottom: 0; }
        // .post-path-quote {
        // font-size: 0.85rem; line-height: 1.6; color: rgba(255,255,255,0.7);
        // font-style: italic;
        // padding-left: 1rem; border-left: 2px solid #b8860b;
        // margin: 0.5rem 0 0 0;
        // }
        // .post-rep-title {
        // font-size: clamp(1.1rem, 1.8vw, 1.4rem);
        // font-weight: 600; color: #ffffff;
        // margin-bottom: 0.75rem;
        // }
        // .post-rep-sub {
        // font-size: 0.95rem; line-height: 1.7; color: rgba(255,255,255,0.65);
        // max-width: 700px; margin-bottom: 1rem;
        // }

        // /* Key Findings */
        // .post-finding-group {
        // margin-bottom: 2.5rem;
        // }
        // .post-finding-group:last-child { margin-bottom: 0; }
        // .post-finding-group-title {
        // font-size: 0.85rem; font-weight: 700; color: #b8860b;
        // text-transform: uppercase; letter-spacing: 0.08em;
        // margin-bottom: 1rem; padding-bottom: 0.5rem;
        // border-bottom: 2px solid rgba(184,134,11,0.2);
        // }
        // .post-finding-row {
        // display: grid;
        // grid-template-columns: 1fr 1fr 1fr;
        // gap: 1.5rem; padding: 1.25rem 0;
        // border-bottom: 1px solid rgba(0,0,0,0.06);
        // }
        // .post-finding-row:last-child { border-bottom: none; }
        // .post-finding-row h4 {
        // font-size: 0.9rem; font-weight: 600; margin: 0 0 0.5rem 0;
        // color: rgba(0,0,0,0.85);
        // }
        // .post-finding-row p {
        // font-size: 0.85rem; line-height: 1.6; color: rgba(0,0,0,0.65);
        // margin: 0;
        // }
        // .post-finding-layer {
        // font-size: 0.7rem; font-weight: 700; color: #b8860b;
        // text-transform: uppercase; letter-spacing: 0.08em;
        // margin-bottom: 0.35rem;
        // }

        // /* Insight Summary */
        // .post-insight-summary {
        // text-align: center;
        // }
        // .post-insight-summary .post-insight-text {
        // font-size: clamp(1.2rem, 2vw, 1.6rem);
        // font-weight: 500; color: #ffffff;
        // line-height: 1.6; max-width: 800px;
        // margin: 0 auto;
        // }

        // /* Design Recommendations */
        // .post-recs-grid {
        // display: grid;
        // grid-template-columns: repeat(3, 1fr);
        // gap: 1.5rem; margin-top: 2rem;
        // }
        // .post-recs-card {
        // background: #ffffff; border-radius: 12px;
        // padding: 1.75rem;
        // box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        // }
        // .post-recs-card-num {
        // font-size: 0.7rem; font-weight: 700; color: #b8860b;
        // letter-spacing: 0.08em; margin-bottom: 0.5rem;
        // }
        // .post-recs-card h4 {
        // font-size: 1rem; font-weight: 600; margin: 0 0 0.5rem 0;
        // color: rgba(0,0,0,0.85);
        // }
        // .post-recs-card p {
        // font-size: 0.85rem; line-height: 1.6; color: rgba(0,0,0,0.6);
        // margin: 0;
        // }

        // /* What I Demonstrated */
        // .post-demo-title {
        // font-size: clamp(1.1rem, 1.8vw, 1.4rem);
        // font-weight: 600; color: rgba(0,0,0,0.85);
        // text-align: center; margin-bottom: 2rem;
        // }
        // .post-demo-grid {
        // display: grid;
        // grid-template-columns: repeat(3, 1fr);
        // gap: 1.5rem;
        // }
        // .post-demo-card {
        // background: #ffffff; border-radius: 12px;
        // padding: 1.75rem; text-align: center;
        // box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        // }
        // .post-demo-card h4 {
        // font-size: 1rem; font-weight: 600; margin: 0 0 0.5rem 0;
        // color: rgba(0,0,0,0.85);
        // }
        // .post-demo-card p {
        // font-size: 0.85rem; line-height: 1.6; color: rgba(0,0,0,0.6);
        // margin: 0;
        // }

        // /* CTA */
        // .post-cta-wrap {
        // text-align: center; padding: 4rem 0;
        // }

        // /* Image scales */
        // .post-img-full {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // display: block;
        // }
        // .post-img-full-constrained {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // display: block; max-height: 70vh; object-fit: cover;
        // }
        // .post-img-medium {
        // width: 85%; display: block; margin: 0 auto;
        // border-radius: 8px;
        // }

        // /* Animations */
        // @keyframes postFadeUp {
        // from { opacity: 0; transform: translateY(24px); }
        // to { opacity: 1; transform: translateY(0); }
        // }
        // .post-animate {
        // animation: postFadeUp 0.6s ease both;
        // }

        // /* Responsive */
        // @media (max-width: 768px) {
        // .post-framing-grid { grid-template-columns: 1fr; }
        // .post-task-journey { flex-direction: column; }
        // .post-ta-grid { grid-template-columns: 1fr; }
        // .post-finding-row { grid-template-columns: 1fr; gap: 0.75rem; }
        // .post-finding-direction { grid-column: 1 / -1; }
        // .post-recs-grid { grid-template-columns: 1fr; }
        // .post-demo-grid { grid-template-columns: 1fr; }
        // .post-numbers { flex-direction: column; gap: 1.5rem; }
        // .post-process-flow { grid-template-columns: 1fr; }
        // .post-snapshot-inner { gap: 1rem; }
        // }
        // /* Test Scenario */
        // .post-scenario {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // background: #fafaf8;
        // padding: 5rem clamp(1.5rem, 5vw, 4rem);
        // position: relative; overflow: hidden;
        // }
        // .post-scenario-inner {
        // max-width: 800px; margin: 0 auto; text-align: center;
        // }
        // .post-scenario-headline {
        // font-size: clamp(1.3rem, 2.2vw, 1.8rem);
        // font-weight: 700; color: rgba(0,0,0,0.85);
        // margin-bottom: 0.75rem;
        // }
        // .post-scenario-sub {
        // font-size: clamp(0.95rem, 1.1vw, 1.05rem);
        // line-height: 1.7; color: rgba(0,0,0,0.6);
        // margin-bottom: 2rem;
        // }

        // /* Insight Summary */
        // .post-insight-summary {
        // width: 100vw; margin-left: calc(-50vw + 50%);
        // background: #0a3d2e;
        // padding: 5rem clamp(1.5rem, 5vw, 4rem);
        // position: relative; overflow: hidden;
        // }
        // .post-insight-summary-inner {
        // max-width: 800px; margin: 0 auto; text-align: center;
        // }
        // .post-insight-big {
        // font-size: clamp(1.4rem, 2.5vw, 2rem);
        // font-weight: 700; color: #ffffff;
        // line-height: 1.3; margin-bottom: 1.25rem;
        // }
        // .post-insight-desc {
        // font-size: clamp(0.95rem, 1.2vw, 1.1rem);
        // line-height: 1.8; color: rgba(255,255,255,0.8);
        // max-width: 700px; margin: 0 auto;
        // }

        // /* Key Findings - extra classes */
        // .post-finding-num {
        // font-size: 0.75rem; font-weight: 700; color: #b8860b;
        // letter-spacing: 0.08em; margin-bottom: 0.35rem;
        // }
        // .post-finding-problem h4 {
        // font-size: 0.9rem; font-weight: 600; margin: 0 0 0.5rem 0;
        // color: rgba(0,0,0,0.85);
        // }
        // .post-finding-problem p {
        // font-size: 0.85rem; line-height: 1.6; color: rgba(0,0,0,0.65);
        // margin: 0;
        // }
        // .post-finding-direction-label {
        // font-size: 0.7rem; font-weight: 700; color: #b8860b;
        // text-transform: uppercase; letter-spacing: 0.08em;
        // margin-bottom: 0.35rem;
        // }

        // /* Representative Case - ensure all text is light on dark bg */
        // .post-fullbleed-dark .post-path-location { color: #b8860b; }
        // .post-fullbleed-dark .post-path-desc { color: rgba(255,255,255,0.75); }
        // .post-fullbleed-dark .post-path-quote { color: rgba(255,255,255,0.7); }
        // .post-fullbleed-dark .post-rep-title { color: #ffffff; }
        // .post-fullbleed-dark .post-rep-sub { color: rgba(255,255,255,0.65); }
        // .post-fullbleed-dark .post-path-num { color: #b8860b; }

        // /* Research Process card list styling */
        // .post-process-step ul {
        // list-style: none; padding: 0; margin: 0.75rem 0 0 0;
        // text-align: left;
        // }
        // .post-process-step ul li {
        // font-size: 0.85rem; line-height: 1.6; color: rgba(0,0,0,0.6);
        // padding: 0.25rem 0; position: relative;
        // padding-left: 1rem;
        // }
        // .post-process-step ul li::before {
        // content: "\u2013"; position: absolute; left: 0;
        // color: #b8860b;
        // }

        // `}</style>
        // {/* HERO - temple style with image as background */}
        // <section className="post-hero">
        // <div className="post-hero-bg">
        // <img
        // src="/images/post.gov.tw.jpg"
        // alt="Chunghwa Post global website homepage showing dense navigation, multiple entry points, and information-heavy layout"
        // />
        // </div>
        // <div className="post-hero-overlay" />
        // <div className="post-hero-content">
        // <div className="post-hero-label">UX Research Case Study</div>
        // <h1>Usability Study: Chunghwa Post Global Website</h1>
        // <p className="post-hero-desc">
        // Using the Think Aloud protocol to identify usability issues in information
        // architecture and process guidance on the Chunghwa Post website.
        // </p>
        // </div>
        // <div className="post-hero-gradient-bottom" />
        // </section>
        // <div className="post-snapshot">
        // <div className="post-snapshot-inner">
        // <div className="post-snapshot-item">
        // <span className="post-snapshot-label">Role</span>
        // <span className="post-snapshot-value">Lead Researcher</span>
        // </div>
        // <div className="post-snapshot-item">
        // <span className="post-snapshot-label">Date</span>
        // <span className="post-snapshot-value">2024</span>
        // </div>
        // <div className="post-snapshot-item">
        // <span className="post-snapshot-label">Scope</span>
        // <span className="post-snapshot-value">
        // User Research, Think Aloud Protocol, Task Design, Website Usability Analysis,
        // Design Recommendation Synthesis
        // </span>
        // </div>
        // <div className="post-snapshot-item">
        // <span className="post-snapshot-label">Team</span>
        // <span className="post-snapshot-value">
        // Six-person research team, advised by Prof. Hsien-Hui Tang
        // </span>
        // </div>
        // </div>
        // </div>
        // {/* OVERVIEW */}
        // <div className="post-section" style={{ paddingTop: '5rem' }}>
        // <div className="post-inner-narrow">
        // <div className="post-section-label">Overview</div>
        // <p>
        // Chunghwa Post is Taiwan's primary postal service provider, covering diverse
        // scenarios including postal services, financial services, e-commerce, and digital
        // payments. As service digitalization became an important development direction, the
        // global website became a key entry point for the public to access postal services.
        // </p>
        // <p>
        // But when users need to complete a more complex task, can the website truly help
        // them quickly find information and complete preparations? This study used
        // "preparing to send an international package" as the scenario, observing users'
        // operations on the website through the Think Aloud protocol to identify usability
        // issues in information architecture, content presentation, process guidance, and
        // interface interaction.
        // </p>
        // </div>
        // </div>

        // {/* RESEARCH QUESTION */}
        // <div className="post-rq">
        // <div className="post-rq-inner">
        // <div className="post-rq-label">Research Question</div>
        // <div className="post-rq-text">
        // How can we help users without international shipping experience accurately and
        // quickly understand the procedures and obtain the necessary information before
        // sending?
        // </div>
        // <p className="post-rq-context">
        // Starting from Chunghwa Post's core service "postal services," we focused on a
        // specific and common shipping scenario — sending an international package. This
        // task was suitable as a research entry point because international package shipping
        // involves multiple information nodes — rate inquiry, required documents, English
        // address preparation — with information scattered across different pages and
        // functions. For novice users, this fully exposes problems in the website's
        // navigation, information classification, and operational flow.
        // </p>
        // </div>
        // </div>

        // {/* RESEARCH FRAMING */}
        // <div className="post-section">
        // <div className="post-inner">
        // <div className="post-section-label">Research Framing</div>
        // <p>
        // I transformed the vague question "Is the Chunghwa Post website easy to use?" into
        // a testable and analyzable research framework through four stages.
        // </p>
        // <div className="post-framing-grid">
        // <div className="post-framing-step">
        // <div className="post-framing-step-num">01 Frame the Problem</div>
        // <h4>Define the Research Scope</h4>
        // <p>
        // Set a specific shipping scenario and three core tasks to transform vague
        // usability questions into testable research.
        // </p>
        // </div>
        // <div className="post-framing-step">
        // <div className="post-framing-step-num">02 Design & Conduct</div>
        // <h4>Execute Think Aloud Protocol</h4>
        // <p>
        // Design task scenarios close to real needs, observe 6 users' real-time
        // operational responses and verbal reactions.
        // </p>
        // </div>
        // <div className="post-framing-step">
        // <div className="post-framing-step-num">03 Synthesize Behavior</div>
        // <h4>Analyze User Behavior</h4>
        // <p>
        // Use Elements of UX as an analytical lens to synthesize scattered usability
        // issues into structured insights.
        // </p>
        // </div>
        // <div className="post-framing-step">
        // <div className="post-framing-step-num">04 Translate Findings</div>
        // <h4>Design Directions</h4>
        // <p>
        // Transform insights into concrete design improvement directions for information
        // architecture, content, and interaction.
        // </p>
        // </div>
        // </div>
        // </div>
        // </div>

        // {/* RESEARCH NUMBERS */}
        // <div className="post-fullbleed-gray">
        // <div className="post-inner">
        // <div className="post-numbers">
        // <div className="post-numbers-item">
        // <div className="post-numbers-num">6</div>
        // <div className="post-numbers-label">Participants</div>
        // </div>
        // <div className="post-numbers-item">
        // <div className="post-numbers-num">3</div>
        // <div className="post-numbers-label">Core Tasks</div>
        // </div>
        // <div className="post-numbers-item">
        // <div className="post-numbers-num">30–35</div>
        // <div className="post-numbers-label">Minutes per Session</div>
        // </div>
        // </div>
        // </div>
        // </div>

        // {/* TEST SCENARIO */}
        // <div className="post-scenario">
        // <div className="post-scenario-inner">
        // <div className="post-section-label" style={{ textAlign: 'center' }}>
        // Test Scenario
        // </div>
        // <div className="post-scenario-headline">
        // Christmas Gift from Taiwan → Netherlands
        // </div>
        // <p className="post-scenario-sub">
        // Participants were asked to prepare sending a gift to a friend in the Netherlands
        // before Christmas
        // </p>
        // <div className="post-task-journey">
        // <div className="post-task-card">
        // <div className="post-task-card-icon">📮</div>
        // <h4>Task 1: Check Rates</h4>
        // <p>
        // Check international parcel shipping rates for a 3kg package (10×5×5 cm) to the
        // Netherlands
        // </p>
        // </div>
        // <div className="post-task-card">
        // <div className="post-task-card-icon">📋</div>
        // <h4>Task 2: Find Documents</h4>
        // <p>Find out what documents are required for shipping an international parcel</p>
        // </div>
        // <div className="post-task-card">
        // <div className="post-task-card-icon">✏️</div>
        // <h4>Task 3: Prepare Address</h4>
        // <p>
        // Prepare the English translation of NTUST's address as the sender's address
        // </p>
        // </div>
        // </div>
        // </div>
        // </div>

        // {/* RESEARCH PROCESS */}
        // <div className="post-section">
        // <div className="post-inner">
        // <div className="post-section-label">Research Process</div>
        // <p>
        // The testing process is divided into three phases: starting with understanding the
        // user's background, having participants operate the website in a near-realistic
        // scenario, and then collecting overall impressions and satisfaction ratings through
        // post-test interviews. Each participant's testing session lasts approximately 30–35
        // minutes.
        // </p>
        // <div className="post-process-flow">
        // <div className="post-process-step">
        // <h4>Pre-test Briefing</h4>
        // <div className="post-process-time">5 min</div>
        // <ul>
        // <li>Research objective explanation</li>
        // <li>Test process explanation</li>
        // <li>Understanding user background and habits</li>
        // </ul>
        // </div>
        // <div className="post-process-step">
        // <h4>Think-aloud Task Testing</h4>
        // <div className="post-process-time">15–20 min</div>
        // <ul>
        // <li>Task 1: Check international parcel rates</li>
        // <li>Task 2: Find required documents</li>
        // <li>Task 3: Prepare English address</li>
        // </ul>
        // </div>
        // <div className="post-process-step">
        // <h4>Post-test Interview</h4>
        // <div className="post-process-time">10 min</div>
        // <ul>
        // <li>Overall impression</li>
        // <li>Impressions of specific pages</li>
        // <li>Overall satisfaction rating 1~5</li>
        // </ul>
        // </div>
        // </div>
        // </div>
        // </div>

        // {/* PARTICIPANTS */}
        // <div className="post-fullbleed-warm">
        // <div className="post-inner">
        // <div className="post-section-label">Participants</div>
        // <p>
        // We recruited 6 young users aged 20–30 to participate in the test. All of them
        // preferred to look up information online beforehand and had no prior experience
        // shipping international parcels. The participants' backgrounds spanned industrial
        // management, computer engineering, mechanical engineering & design, finance,
        // concept art, and marketing planning, allowing the study to observe how users from
        // different backgrounds understand the same tasks.
        // </p>
        // <div style={{ overflowX: 'auto', margin: '1.5rem 0' }}>
        // <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        // <thead>
        // <tr style={{ background: '#ffffff', borderBottom: '2px solid #b8860b' }}>
        // <th
        // style={{
        // padding: '0.75rem 1rem',
        // textAlign: 'left',
        // fontWeight: 700,
        // color: '#1f2937',
        // }}
        // >
        // ID
        // </th>
        // <th
        // style={{
        // padding: '0.75rem 1rem',
        // textAlign: 'left',
        // fontWeight: 700,
        // color: '#1f2937',
        // }}
        // >
        // Name
        // </th>
        // <th
        // style={{
        // padding: '0.75rem 1rem',
        // textAlign: 'left',
        // fontWeight: 700,
        // color: '#1f2937',
        // }}
        // >
        // Age
        // </th>
        // <th
        // style={{
        // padding: '0.75rem 1rem',
        // textAlign: 'left',
        // fontWeight: 700,
        // color: '#1f2937',
        // }}
        // >
        // Occupation
        // </th>
        // </tr>
        // </thead>
        // <tbody>
        // {[
        // ['P1', 'Ping', '23', 'Student — Industrial Management'],
        // ['P2', 'Leo', '28', 'Computer Engineer'],
        // ['P3', 'Kuo', '27', 'Student — Mechanical Engineering & Design'],
        // ['P4', 'Tony', '24', 'Student — Finance'],
        // ['P5', 'Yang', '28', 'Concept Artist'],
        // ['P6', 'Giin', '25', 'Marketing Planner'],
        // ].map((row, idx) => (
        // <tr
        // key={idx}
        // style={{
        // borderBottom: '1px solid #e2e8f0',
        // background: idx % 2 === 0 ? '#ffffff' : '#fafaf8',
        // }}
        // >
        // <td
        // style={{ padding: '0.85rem 1rem', fontWeight: 600, color: '#b8860b' }}
        // >
        // {row[0]}
        // </td>
        // <td
        // style={{ padding: '0.85rem 1rem', fontWeight: 600, color: '#1f2937' }}
        // >
        // {row[1]}
        // </td>
        // <td style={{ padding: '0.85rem 1rem', color: '#475569' }}>{row[2]}</td>
        // <td style={{ padding: '0.85rem 1rem', color: '#475569' }}>{row[3]}</td>
        // </tr>
        // ))}
        // </tbody>
        // </table>
        // </div>
        // </div>
        // </div>

        // {/* THINK ALOUD EVIDENCE */}
        // <div className="post-section">
        // <div className="post-inner">
        // <div className="post-section-label">Think Aloud Evidence</div>
        // <p>
        // During data organization, operational behaviors, page transitions, and verbal
        // responses were annotated as analysis records to further identify users' points of
        // hesitation, disorientation, and frustration during tasks.
        // </p>
        // <div className="post-ta-grid">
        // <div className="post-ta-img-wrap">
        // <img
        // src="/images/post record.jpg"
        // alt="Participant operation record annotation diagram showing page navigation, verbal reactions, pauses, wrong turns, and terminology confusion"
        // style={{ width: '100%', height: 'auto', display: 'block' }}
        // />
        // </div>
        // <div className="post-ta-annotations">
        // <div className="post-ta-item">
        // <div className="post-ta-dot" />
        // <span>
        // <strong>Page navigation</strong> — Users frequently switched between
        // multiple entry points and categories
        // </span>
        // </div>
        // <div className="post-ta-item">
        // <div className="post-ta-dot" />
        // <span>
        // <strong>Verbal reactions</strong> — Real-time expressions of confusion,
        // surprise, and realization during tasks
        // </span>
        // </div>
        // <div className="post-ta-item">
        // <div className="post-ta-dot" />
        // <span>
        // <strong>Pauses and hesitation</strong> — Moments where users stopped to
        // decide which path to take
        // </span>
        // </div>
        // <div className="post-ta-item">
        // <div className="post-ta-dot" />
        // <span>
        // <strong>Wrong turns</strong> — Users entered incorrect sections and had to
        // backtrack
        // </span>
        // </div>
        // <div className="post-ta-item">
        // <div className="post-ta-dot" />
        // <span>
        // <strong>Terminology confusion</strong> — Postal jargon like "special rates"
        // and "rolled items" caused comprehension gaps
        // </span>
        // </div>
        // </div>
        // </div>
        // </div>
        // </div>

        // {/* REPRESENTATIVE CASE */}
        // <div className="post-fullbleed-dark">
        // <div className="post-inner">
        // <div className="post-section-label">Representative Case</div>
        // <div className="post-rep-title">
        // Task 1: The User's Journey from Homepage to Rate Inquiry
        // </div>
        // <p className="post-rep-sub">
        // When checking international parcel rates, users had to navigate between multiple
        // entry points starting from the homepage, encountering confusion about postal
        // classifications and professional terminology along the way.
        // </p>
        // <div className="post-user-path">
        // <div className="post-path-step">
        // <div className="post-path-location">Homepage</div>
        // <div className="post-path-content">
        // <p className="post-path-desc">
        // User arrives at the Chunghwa Post global website homepage with dense
        // navigation and multiple entry points
        // </p>
        // </div>
        // </div>
        // <div className="post-path-step">
        // <div className="post-path-location">Choose Entrance</div>
        // <div className="post-path-content">
        // <p className="post-path-desc">
        // User must decide between "Postal Services," "International Mail," or other
        // categories
        // </p>
        // <p className="post-path-quote">
        // "After entering the page, I realized it wasn't the international parcel
        // inquiry, so I went to look on the left side, wondering whether it was letter
        // or parcel? Finally chose parcel."
        // </p>
        // </div>
        // </div>
        // <div className="post-path-step">
        // <div className="post-path-location">Wrong Category</div>
        // <div className="post-path-content">
        // <p className="post-path-desc">
        // User enters "函件" (Letter items) instead of "包裹" (Parcel) — unclear
        // classification
        // </p>
        // <p className="post-path-quote">
        // "不確定應該選函件還是包裹" (Not sure whether to choose letter or parcel)
        // </p>
        // </div>
        // </div>
        // <div className="post-path-step">
        // <div className="post-path-location">Letters vs Parcels</div>
        // <div className="post-path-content">
        // <p className="post-path-desc">
        // User hesitates between "International Letter Items" and "International
        // Parcels" — the distinction is not intuitive for novice users
        // </p>
        // </div>
        // </div>
        // <div className="post-path-step">
        // <div className="post-path-location">Search Destination</div>
        // <div className="post-path-content">
        // <p className="post-path-desc">
        // User reaches the rate inquiry page but struggles with the country selection
        // dropdown
        // </p>
        // <p className="post-path-quote">
        // "The dropdown menu made it hard to find the desired country, so I used the
        // input field instead."
        // </p>
        // </div>
        // </div>
        // <div className="post-path-step">
        // <div className="post-path-location">Interpret Terminology</div>
        // <div className="post-path-content">
        // <p className="post-path-desc">
        // User encounters unfamiliar postal terms like "special rates" and "rolled
        // items"
        // </p>
        // <p className="post-path-quote">
        // "Special rates also didn't specify what kind of 'special' they were?"
        // </p>
        // <p className="post-path-quote">"I don't know what 'rolled items' means?"</p>
        // </div>
        // </div>
        // </div>
        // </div>
        // </div>

        // {/* KEY FINDINGS */}
        // <div className="post-section">
        // <div className="post-inner">
        // <div className="post-section-label">Key Findings</div>
        // <p>
        // The difficulties users encountered on the Chunghwa Post website stemmed from a
        // lack of clear pathways in overall information organization and task guidance. We
        // compared the common issues across all participants and used the Elements of UX as
        // an analytical lens, summarizing six major problems into three groups.
        // </p>

        // {/* Group 1: Findability */}
        // <div className="post-finding-group">
        // <div className="post-finding-group-title">Findability — 01–02</div>
        // <div className="post-finding-row">
        // <div className="post-finding-num">01</div>
        // <div className="post-finding-problem">
        // <h4>Unclear Information Architecture Hierarchy</h4>
        // <p>
        // The website has numerous duplicate entry points and categories, requiring
        // users to determine which path is relevant to their task, increasing
        // trial-and-error costs.
        // </p>
        // </div>
        // <div className="post-finding-direction">
        // <div className="post-finding-direction-label">Design Direction</div>
        // <p>
        // Reorganize the information architecture, simplify homepage entry points, and
        // establish clearer navigation paths.
        // </p>
        // </div>
        // </div>
        // <div className="post-finding-row">
        // <div className="post-finding-num">02</div>
        // <div className="post-finding-problem">
        // <h4>Unclear Priority of Key Information</h4>
        // <p>
        // The information users need most is not placed in easily noticeable
        // positions, often being overlooked due to lengthy pages or unclear emphasis.
        // </p>
        // </div>
        // <div className="post-finding-direction">
        // <div className="post-finding-direction-label">Design Direction</div>
        // <p>
        // Reorganize content according to user task sequences and reinforce important
        // information through visual cues.
        // </p>
        // </div>
        // </div>
        // </div>

        // {/* Group 2: Guidance and Comprehension */}
        // <div className="post-finding-group">
        // <div className="post-finding-group-title">Guidance and Comprehension — 03–04</div>
        // <div className="post-finding-row">
        // <div className="post-finding-num">03</div>
        // <div className="post-finding-problem">
        // <h4>Scattered Information and Lack of Process Guidance</h4>
        // <p>
        // International parcel shipping involves multiple steps and documents, but
        // related information is scattered across different pages, making it difficult
        // for users to confirm whether they have completed all preparations.
        // </p>
        // </div>
        // <div className="post-finding-direction">
        // <div className="post-finding-direction-label">Design Direction</div>
        // <p>
        // Consolidate scattered information, add flowcharts or step-by-step guidance,
        // and reduce page switching.
        // </p>
        // </div>
        // </div>
        // <div className="post-finding-row">
        // <div className="post-finding-num">04</div>
        // <div className="post-finding-problem">
        // <h4>Obscure Terminology and Excessive Jargon</h4>
        // <p>
        // Postal terms such as "letter items," "special rates," "rolled items," and
        // "submission" are unintuitive for novice users and easily cause comprehension
        // gaps.
        // </p>
        // </div>
        // <div className="post-finding-direction">
        // <div className="post-finding-direction-label">Design Direction</div>
        // <p>
        // Use more accessible language and supplement with terminology explanations
        // and visual aids.
        // </p>
        // </div>
        // </div>
        // </div>

        // {/* Group 3: Interaction and Readability */}
        // <div className="post-finding-group">
        // <div className="post-finding-group-title">
        // Interaction and Readability — 05–06
        // </div>
        // <div className="post-finding-row">
        // <div className="post-finding-num">05</div>
        // <div className="post-finding-problem">
        // <h4>Unintuitive UI Components and Interaction Methods</h4>
        // <p>
        // Some fields, tooltips, and dropdown menus do not align with users' existing
        // experience, requiring additional judgment during operation.
        // </p>
        // </div>
        // <div className="post-finding-direction">
        // <div className="post-finding-direction-label">Design Direction</div>
        // <p>
        // Optimize field hints, error messages, and menu interactions to align
        // operation methods with user expectations.
        // </p>
        // </div>
        // </div>
        // <div className="post-finding-row">
        // <div className="post-finding-num">06</div>
        // <div className="post-finding-problem">
        // <h4>Text Length and Layout Affect Reading Comfort</h4>
        // <p>
        // Some pages have excessively long text, inconsistent font sizes, and layout,
        // increasing the burden of reading and comprehension, which is particularly
        // unfriendly to elderly users.
        // </p>
        // </div>
        // <div className="post-finding-direction">
        // <div className="post-finding-direction-label">Design Direction</div>
        // <p>
        // Establish text and layout standards to improve readability and overall
        // reading experience.
        // </p>
        // </div>
        // </div>
        // </div>
        // </div>
        // </div>

        // {/* INSIGHT SUMMARY */}
        // <div className="post-insight-summary">
        // <div className="post-insight-summary-inner">
        // <div className="post-insight-big">The problem was not one isolated page.</div>
        // <p className="post-insight-desc">
        // The website lacked a clear task-oriented path. Users could not establish a
        // coherent mental model of where to start, what steps to follow, and whether they
        // had completed all necessary preparations. Each usability issue, from unclear
        // navigation to confusing terminology, compounded the overall difficulty of
        // completing even a straightforward shipping task.
        // </p>
        // </div>
        // </div>

        // {/* DESIGN RECOMMENDATIONS */}
        // <div className="post-section">
        // <div className="post-inner">
        // <div className="post-section-label">Design Recommendations</div>
        // <p>
        // Based on the six key findings, I proposed the following design improvement
        // directions to address the root causes of usability issues.
        // </p>
        // <div className="post-recs-grid">
        // <div className="post-recs-card">
        // <div className="post-recs-card-num">01</div>
        // <h4>Simplify Navigation</h4>
        // <p>
        // Reorganize information architecture, reduce duplicate entry points, and create
        // clearer navigation paths aligned with user tasks.
        // </p>
        // </div>
        // <div className="post-recs-card">
        // <div className="post-recs-card-num">02</div>
        // <h4>Organize Content Around Tasks</h4>
        // <p>
        // Prioritize content based on user task sequences and use visual hierarchy to
        // guide attention to the most important information.
        // </p>
        // </div>
        // <div className="post-recs-card">
        // <div className="post-recs-card-num">03</div>
        // <h4>Step-by-Step Guidance</h4>
        // <p>
        // Consolidate scattered information into a unified workflow with clear progress
        // indicators and step-by-step instructions.
        // </p>
        // </div>
        // <div className="post-recs-card">
        // <div className="post-recs-card-num">04</div>
        // <h4>Replace Terminology</h4>
        // <p>
        // Use plain language for postal terms and supplement with tooltips, icons, and
        // visual aids to reduce comprehension barriers.
        // </p>
        // </div>
        // <div className="post-recs-card">
        // <div className="post-recs-card-num">05</div>
        // <h4>Improve Interactions</h4>
        // <p>
        // Optimize form fields, dropdown menus, and error messages to align with
        // established UI patterns and user expectations.
        // </p>
        // </div>
        // <div className="post-recs-card">
        // <div className="post-recs-card-num">06</div>
        // <h4>Content Hierarchy</h4>
        // <p>
        // Establish consistent typography and layout standards to improve readability,
        // especially for content-heavy pages.
        // </p>
        // </div>
        // </div>
        // </div>
        // </div>

        // {/* WHAT I DEMONSTRATED */}
        // <div className="post-fullbleed-warm">
        // <div className="post-inner">
        // <div className="post-demo-title">
        // Turning observed behavior into actionable design direction
        // </div>
        // <div className="post-demo-grid">
        // <div className="post-demo-card">
        // <h4>Research Framing</h4>
        // <p>
        // Narrowing down the broad question "Is the Chunghwa Post website
        // user-friendly?" into a specific international shipping scenario with three
        // core tasks, making the research close to real user needs.
        // </p>
        // </div>
        // <div className="post-demo-card">
        // <h4>User Testing & Analysis</h4>
        // <p>
        // Using the Think Aloud method to observe users' real-time thoughts, pauses, and
        // confusion, and organizing operation records into analyzable user behavior
        // data.
        // </p>
        // </div>
        // <div className="post-demo-card">
        // <h4>Insight to Design Direction</h4>
        // <p>
        // Using the Elements of UX as an analytical lens to consolidate scattered
        // usability issues into six key insights, and proposing corresponding design
        // improvement directions.
        // </p>
        // </div>
        // </div>
        // </div>
        // </div>

        // {/* CTA */}
        // <div className="post-cta-wrap">
        // <a
        // href="https://medium.com/ditl/%E5%A6%82%E4%BD%95%E5%B0%87%E5%8F%A3%E8%AA%9E%E5%88%86%E6%9E%90%E6%B3%95%E8%88%87-elements-of-ux-%E6%87%89%E7%94%A8%E6%96%BC%E7%B6%B2%E7%AB%99%E4%BD%BF%E7%94%A8%E8%80%85%E9%AB%94%E9%A9%97%E5%88%86%E6%9E%90-805628f7508d"
        // target="_blank"
        // rel="noopener noreferrer"
        // style={{
        // display: 'inline-flex',
        // alignItems: 'center',
        // gap: '0.5rem',
        // background: '#1f2937',
        // color: '#ffffff',
        // padding: '1rem 2rem',
        // fontWeight: 400,
        // fontSize: '1.05rem',
        // textDecoration: 'none',
        // borderRadius: '999px',
        // transition: 'all 0.3s ease',
        // }}
        // >
        // Want to learn more about the complete research design and insight details? Read the
        // Medium article <span style={{ marginLeft: '0.25rem' }}>→</span>
        // </a>
        // </div>
        // </div>
        //   )
      } else if (isYilan) {
        return <YilanTeacherPerks proj={proj} />
      }
      // [Commented out] Digital Learning Support Platform
      // else if (isSupport) {
      //   return <ISupportServiceSystem proj={proj} />
      // }
      else {
        return (
          /* Generic Detail View Template for other projects */
          <div className="case-study-container">
            <div className="case-study-hero" style={{ background: proj.color }}>
              <h1 className="case-study-hero-title">{proj.title}</h1>
              <p className="case-study-hero-tagline">{proj.tagline}</p>
            </div>

            <div className="case-study-content-grid">
              <TableOfContents />
              <div className="case-study-main-content">
                <div className="case-study-meta-grid">
                  <div className="case-study-meta-item">
                    <span className="case-study-meta-label">ROLE</span>
                    <span className="case-study-meta-val">{proj.role}</span>
                  </div>
                  <div className="case-study-meta-item">
                    <span className="case-study-meta-label">TIMELINE</span>
                    <span className="case-study-meta-val">{proj.timeline}</span>
                  </div>
                  <div className="case-study-meta-item">
                    <span className="case-study-meta-label">PLATFORM</span>
                    <span className="case-study-meta-val">{proj.platform}</span>
                  </div>
                </div>

                <div className="case-study-body">
                  <div className="case-study-section">
                    <h3 className="case-study-section-title">1. The Problem</h3>
                    <p>{proj.problem}</p>
                  </div>

                  <div className="case-study-section">
                    <h3 className="case-study-section-title">2. UX Research & Insights</h3>
                    <p>{proj.research}</p>
                  </div>

                  <div className="case-study-section">
                    <h3 className="case-study-section-title">3. The Solution</h3>
                    <p>{proj.solution}</p>

                    <div className="proto-screen-mock">
                      <div
                        style={{
                          background: '#f5f5f3',
                          padding: '0.5rem',
                          borderBottom: '1px solid #e2e8f0',
                          display: 'flex',
                          gap: '4px',
                        }}
                      >
                        <div
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#ef4444',
                          }}
                        ></div>
                        <div
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#eab308',
                          }}
                        ></div>
                        <div
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#22c55e',
                          }}
                        ></div>
                      </div>
                      <div
                        style={{
                          padding: '1.5rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              width: '45%',
                              height: '8px',
                              background: '#cbd5e1',
                              borderRadius: '4px',
                            }}
                          ></div>
                          <div
                            style={{
                              width: '20%',
                              height: '12px',
                              background: '#f5f5f3',
                              borderRadius: '6px',
                            }}
                          ></div>
                        </div>
                        <div
                          style={{
                            height: '90px',
                            background: proj.color,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Eye size={24} style={{ color: '#2d6a4f' }} />
                        </div>
                        <div
                          style={{
                            width: '100%',
                            height: '6px',
                            background: '#f5f5f3',
                            borderRadius: '3px',
                          }}
                        ></div>
                        <div
                          style={{
                            width: '85%',
                            height: '6px',
                            background: '#f5f5f3',
                            borderRadius: '3px',
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="case-study-section">
                    <h3 className="case-study-section-title">4. Validation & Impact</h3>
                    <p>{proj.validation}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
    }

    return <div className="case-study-container">{renderDetailView()}</div>
  }

  // --- RENDERING WORK LIST CARD ---
  const renderWorkCard = (proj: Project) => {
    // Strip year prefix from category for the homepage card display
    const displayCategory = proj.category
      .replace(/^[\d]{4}[\d\-.－]*\s*・\s*/, '') // Remove leading year(s) like "2024・" or "2024-2026・" or "2024.09－2024.11・"
      .split('・')
      .join(' · ')

    const isPostOffice = proj.id === 'post-office-ux-research'
    return (
      <button
        key={proj.id}
        className="work-list-card"
        onClick={() => {
          if (isPostOffice) {
            window.open(
              'https://medium.com/ditl/%E5%A6%82%E4%BD%95%E5%B0%87%E5%8F%A3%E8%AA%9E%E5%88%86%E6%9E%90%E6%B3%95%E8%88%87-elements-of-ux-%E6%87%89%E7%94%A8%E6%96%BC%E7%B6%B2%E7%AB%99%E4%BD%BF%E7%94%A8%E8%80%85%E9%AB%94%E9%A9%97%E5%88%86%E6%9E%90-805628f7508d',
              '_blank',
              'noopener,noreferrer'
            )
          } else {
            router.push(`/work/${proj.id}`)
          }
        }}
        role="button"
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          color: 'inherit',
          textAlign: 'inherit',
          width: '100%',
        }}
      >
        <div className="work-list-card-image-wrap">{renderMockup(proj.id)}</div>
        <div className="work-list-card-info">
          <h3 className="work-list-card-title">{proj.title}</h3>
          <span className="work-list-card-category">{displayCategory}</span>
        </div>
      </button>
    )
  }

  const uiuxProjects = projects.filter(
    (p) => !p.draft && !['bookstore-usability-research', 'post-office-ux-research'].includes(p.id)
  )

  return (
    <div className="port-section">
      {!selectedProject && (
        <div
          className="work-intro-header"
          style={{
            marginBottom: '3rem',
            position: 'relative',
            maxWidth: 'var(--port-max-width)',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <h2
              className="section-title"
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '1.15rem',
                fontWeight: 800,
                letterSpacing: '-0.02em',
                margin: 0,
                textAlign: 'center',
              }}
            >
              ＃Selected Works
            </h2>
          </div>
        </div>
      )}

      {!selectedProject && (
        <div className="work-list">{uiuxProjects.map((proj) => renderWorkCard(proj))}</div>
      )}
    </div>
  )
}
